/**
 * Black Marble — main wiring (FTR4 renderer + FTR5 flight/camera + FTR6 HUD +
 * FTR5b playable controls). Loop: read input -> step ship sim (fixed 120Hz)
 * -> advance world (Keplerian) -> rebase render world to ship (floating origin)
 * -> update cameras/HUD -> composer.render.
 */
import * as THREE from 'three';
import { getSettings, settingsStore } from './settings/store.js';
import './hud/hud.css';
import {
  vec3,
  rotateVec3,
  conjugate,
  distance,
  sub,
  STATIONS,
  stationById,
  stepDocking,
  nearestStationContext,
  initialDockingState,
  REQUEST_MAX_SPEED_KMS,
  MIN_ALIGNMENT,
  makeTraffic,
  stepNpc,
  nextLeg,
  NPC_ARRIVAL_SNAP_KM,
  fireWeapon,
  stepProjectile,
  projectileExpired,
  projectileHits,
  applyDamage,
  makeCombatant,
  isDead,
  npcName,
  HIT_RADIUS_KM,
  WEAPON_ENERGY_COST,
  FIRE_COOLDOWN_SEC,
  BOLT_DAMAGE,
  NPC_BOLT_DAMAGE,
  NPC_FIRE_COOLDOWN_SEC,
  generateMissions,
  initialWallet,
  initialCargo,
  addCargo,
  cargoUsed,
  DEFAULT_CARGO_CAPACITY,
  resolveOwnedShipId,
  neutralFactionStanding,
  type FactionStanding,
  SAVE_VERSION,
  makeObjectives,
  validateBeatTable,
  CAMPAIGN_BEATS,
  stepObjective,
  failObjective,
  objectiveHudLine,
  credit,
  makeStationTraffic,
  regionFaction,
  positionSeed,
  type FactionId,
  aimDirection,
  BOLT_SPEED_KMS,
  makeCombatBody,
  computeNpcCombatCommand,
  stepNpcCombatPhysics,
  type NpcCombatEnv,
  type RigidBody,
  type Vec3,
  type Quat,
  type DockingPhase,
  type StationContext,
  type NpcShip,
  type Projectile,
  type Combatant,
  type Wallet,
  type CargoHold,
  type Mission,
  type SaveState,
  type Objective,
  type ObjectiveEvent,
  shouldDropIntoSignal,
  verifySignal,
  buildVerificationModuleView,
  signalMarkerPos,
  type VerifyStatus,
  type SignalSource,
  type SignalKind,
  type StationSpec,
} from '@blackmarble/sim-core';
import { World, loadManifest } from './sim/world.js';
import { createScene, SUN_SHADOW_LIGHT_DISTANCE_KM, type SceneBundle } from './render/scene.js';
import { BodyRenderer } from './render/bodies.js';
import { AsteroidBelt } from './render/belt.js';
import { createStarfield, followCamera } from './render/starfield.js';
import { loadSkybox } from './render/skybox.js';
import { SunGlare, sphereOcclusion01 } from './render/sunGlare.js';
import { CameraRig } from './render/cameras.js';
import { FlightFeel } from './render/flightFeel.js';
import { SpeedLines } from './render/speedLines.js';
import { SupercruiseWarp } from './render/supercruiseWarp.js';
import { CockpitView } from './render/cockpit.js';
import { SignalManager } from './sim/signals.js';
import { SignalRenderer } from './render/signals.js';
import { SalvageRenderer } from './render/salvage.js';
import { CenotaphRenderer } from './render/cenotaph.js';
import { OrbitLines } from './render/orbitLines.js';
import { AnomalyFxRenderer } from './render/anomalyFx.js';
import { loadPlayerShip, type PlayerShipRenderable } from './render/playerShip.js';
import { WeaponMounts } from './render/weaponMounts.js';
import { assetDir } from './render/assetDir.js';
import { Input, type InputState } from './input/input.js';
import { displayBindingKey, type BindingAction } from './input/keymap.js';
import { ShipController } from './sim/shipController.js';
import {
  shipById,
  applyLoadout,
  STARTER_MODULES,
  type EffectiveShip,
  type ShipSpec,
  type Loadout,
} from '@blackmarble/sim-core';
import { loadoutStore, getActive } from './sim/loadoutStore.js';
import { TargetManager, closingSpeedTo } from './travel/target.js';
import { Supercruise, type CruiseState } from './travel/supercruise.js';
import { Hud, type HudFrame } from './hud/hud.js';
import { StartupOverlay } from './hud/startupOverlay.js';
import { TitleScreen } from './hud/titleScreen.js';
import { MainMenu, type MenuSaveSummary } from './hud/mainMenu.js';
import { CreditsScreen } from './hud/creditsScreen.js';
import { PauseMenu } from './hud/pauseMenu.js';
import { SettingsScreen } from './hud/settingsScreen.js';
import { VerificationModule } from './hud/verificationModule.js';
import { StationRenderer } from './render/stations.js';
import { NpcRenderer } from './render/npcs.js';
import {
  HOSTILE_BOLT_COLOR,
  PLAYER_BOLT_COLOR,
  ProjectileRenderer,
} from './render/projectiles.js';
import { CombatFxRenderer } from './render/combatFx.js';
import { Radar3D } from './render/radar3d.js';
import { ServicesMenu, type EconomyBinding } from './hud/servicesMenu.js';
import { TargetMenu, type TargetGroup } from './hud/targetMenu.js';
import { fmtDistance } from './hud/format.js';
import { initTunePanel, type TuneEntry, type TuneGroup } from './hud/tunePanel.js';
import { DeathOverlay } from './hud/deathOverlay.js';
import { CommsPanel } from './hud/commsPanel.js';
import { ChapterCard } from './hud/chapterCard.js';
import { CampaignDirector } from './campaign/director.js';
import { CloudSaves, offerNewerRecord } from './save/cloudSave.js';
import { AudioEngine } from './audio/audioEngine.js';
import { MusicManager, selectMusicContext } from './audio/musicManager.js';
import { IntroAudio } from './audio/introAudio.js';
import { IntroCinematic } from './hud/introCinematic.js';
import { shouldPlayIntro } from './hud/introTimeline.js';
import { bindRuntimeSettings } from './settings/runtime.js';
import { loadGame, saveGame } from './save/saveStore.js';
import { acquireCombatTarget, type CombatLock } from './travel/target.js';
import type { DockingState } from '@blackmarble/sim-core';
import { setCameraFeelTuning, setFlightFeelTuning } from './feelTuning.js';

const ROLL_HOLD_SEC = 8; // M2 P4: a death name lingers on the Roll this long (matches hud.ts)

const tune = (
  label: string,
  min: number,
  max: number,
  step: number,
  get: () => number,
  set: (value: number) => void,
): TuneEntry => ({
  label,
  min,
  max,
  step,
  get,
  set,
});

const stickTuneEntries = (ship: ShipController): TuneEntry[] => [
  tune('px to full', 80, 600, 10, () => ship.stickParams.pxToFull, (v) => { ship.stickParams.pxToFull = v; }),
  tune('relax /s', 0, 8, 0.25, () => ship.stickParams.relaxPerSec, (v) => { ship.stickParams.relaxPerSec = v; }),
  tune('deadzone', 0, 0.15, 0.01, () => ship.stickParams.deadzone, (v) => { ship.stickParams.deadzone = v; }),
  tune('expo', 1, 2.5, 0.05, () => ship.stickParams.expo, (v) => { ship.stickParams.expo = v; }),
];

const cameraTuneEntries = (rig: CameraRig): TuneEntry[] => [
  tune('aim chase (s)', 0, 0.4, 0.01, () => rig.tuning.tauChase, (v) => { setCameraFeelTuning(rig.tuning, 'tauChase', v); }),
  tune('aim cockpit (s)', 0, 0.1, 0.005, () => rig.tuning.tauCockpit, (v) => { setCameraFeelTuning(rig.tuning, 'tauCockpit', v); }),
  tune('frame chase (s)', 0, 2, 0.05, () => rig.tuning.tauFrameChase, (v) => { setCameraFeelTuning(rig.tuning, 'tauFrameChase', v); }),
  tune('frame cockpit (s)', 0, 0.5, 0.01, () => rig.tuning.tauFrameCockpit, (v) => { setCameraFeelTuning(rig.tuning, 'tauFrameCockpit', v); }),
];

const feelTuneEntries = (feel: FlightFeel): TuneEntry[] => [
  tune('fov speed widen', 0, 16, 0.25, () => feel.tuning.speedFov, (v) => { setFlightFeelTuning(feel.tuning, 'speedFov', v); }),
  tune('fov boost widen', 0, 20, 0.25, () => feel.tuning.boostFov, (v) => { setFlightFeelTuning(feel.tuning, 'boostFov', v); }),
  tune('shake thrust', 0, 0.003, 0.0001, () => feel.tuning.thrustShake, (v) => { setFlightFeelTuning(feel.tuning, 'thrustShake', v); }),
  tune('shake speed', 0, 0.003, 0.0001, () => feel.tuning.speedShake, (v) => { setFlightFeelTuning(feel.tuning, 'speedShake', v); }),
  tune('shake boost', 0, 0.008, 0.0001, () => feel.tuning.boostShake, (v) => { setFlightFeelTuning(feel.tuning, 'boostShake', v); }),
  tune('shake combat', 0, 0.03, 0.0005, () => feel.tuning.combatShake, (v) => { setFlightFeelTuning(feel.tuning, 'combatShake', v); }),
  tune('shake position km', 0, 20, 0.25, () => feel.tuning.shakePosKm, (v) => { setFlightFeelTuning(feel.tuning, 'shakePosKm', v); }),
];

const buildTuneGroups = (ship: ShipController, rig: CameraRig, feel: FlightFeel): TuneGroup[] => [
  { label: 'Stick', entries: stickTuneEntries(ship) },
  { label: 'Camera: Aim Lag / Frame Lag', entries: cameraTuneEntries(rig) },
  { label: 'Feel: Boost / Shake / FOV', entries: feelTuneEntries(feel) },
];
const SIM_TIME_SCALE = 1; // 1 wall-second == 1 sim-second
const PROGRADE_PROJECT_DIST = 1000; // how far ahead to project the prograde ball
const RADAR_RANGE_COMBAT = 150;
const RADAR_RANGE_DEFAULT = 2000;
const CENOTAPH_MUSIC_RANGE_KM = 3_000;
const Y_AXIS = new THREE.Vector3(0, 1, 0);

interface Spawn {
  pos: Vec3;
  orientation: Quat;
}

/**
 * M1.1c: spawn ~5000 km off Earth on the SUNWARD side (between Sol and Earth),
 * so Earth's LIT, textured day-side faces the camera (was anti-Sol → night-side
 * black disc). `sunward` = normalize(earthPos) = Sol→Earth; subtract it to sit
 * between Sol and Earth. A perpendicular + elevation offset gives a pleasant
 * crescent/terminator and keeps Luna in frame. Still oriented to look at Earth.
 */
/** AAA-02: one Verification Ledger row — a carved name revealed glyph-by-glyph. */
interface LedgerRow {
  name: string;
  status: VerifyStatus;
  revealChars: number;
}

interface Sim {
  world: World;
  bodyRenderer: BodyRenderer;
  belt: AsteroidBelt;
  starfield: THREE.Points;
  ship: ShipController;
  input: Input;
  camRig: CameraRig;
  targets: TargetManager;
  cruise: Supercruise;
  hud: Hud;
  radar3d: Radar3D;
  cockpit: CockpitView;
  playerShip: PlayerShipRenderable;
  // Wave 3: the modular loadout that drives the player ship. `baseSpec` is the
  // unmodified hull; `effective` = applyLoadout(baseSpec, activeLoadout) and its
  // .spec feeds the flight controller, its .weapons feed combat + the mounts.
  baseSpec: ShipSpec;
  effective: EffectiveShip;
  weaponMounts: WeaponMounts;
  overlay: StartupOverlay;
  pauseMenu: PauseMenu;
  bundle: SceneBundle;
  sunGlare: SunGlare; // WAVE 2a: restrained, occlusion-tested Sol-only glare sprite
  // M2 P2: stations + docking.
  stations: StationRenderer;
  svc: ServicesMenu;
  targetMenu: TargetMenu; // M4: Tab-toggled target selection overlay
  dock: DockingState;
  // M2 P3: AI traffic.
  npcs: NpcShip[];
  npcRenderer: NpcRenderer;
  /** Combat Stage 1: 6DOF rigid bodies for enc-* hostiles (physics-driven flight). */
  npcBodies: Map<string, RigidBody>;
  // M2 P4: combat.
  projectiles: Projectile[];
  projRenderer: ProjectileRenderer;
  combatFx: CombatFxRenderer;
  hp: Map<string, Combatant>;
  combatLock: CombatLock | null;
  fireCooldown: number;
  boltSeed: number;
  roll: { name: string; age: number }[];
  combatShakeImpulse: number;
  // M2 P5: economy + missions.
  wallet: Wallet;
  cargo: CargoHold;
  missions: Mission[];
  activeMission: Mission | null;
  // SHIP-EXPANSION (save v5): hangar — hulls the player owns, per-faction standing,
  // and story/derelict unlock tokens. The active hull (`ship.spec.id`) is always in
  // ownedShips; new games own only Vesper-3.
  ownedShips: string[];
  factionStanding: FactionStanding;
  shipUnlocks: string[];
  // M2 P6: procedural audio.
  audio: AudioEngine;
  // M3 FLIGHT-FEEL A6: client-only juice (dynamic FOV + shake, speed lines).
  flightFeel: FlightFeel;
  speedLines: SpeedLines;
  // FIX 3: distinct supercruise warp-stretch overlay (gated on cruise.active).
  supercruiseWarp: SupercruiseWarp;
  // M3 WORLD B2-B8: living world — signals, encounters, salvage, objectives.
  signalMgr: SignalManager;
  signalRenderer: SignalRenderer;
  salvageRenderer: SalvageRenderer;
  cenotaph: CenotaphRenderer; // AAA-10: the carved-name hero asset
  orbitLines: OrbitLines; // ITEM3: toggleable orbital-lines overlay
  anomalyFx: AnomalyFxRenderer; // procedural anomaly particle FX

  objectives: Objective[];
  activeObjectiveId: string | null;
  // FIX 3: brief flash timers — outgoing hit confirmation + incoming damage cue.
  combatHitFlash: number; // seconds remaining (>0 = player bolt just hit a target)
  combatDmgFlash: number; // seconds remaining (>0 = incoming bolt just hit player)
  // H3 COMBAT READABILITY: per-frame state for the new HUD elements.
  /** Normalised screen-space direction toward the last attacker (for edge indicator). */
  combatDmgDirAngle: number; // radians, screen-space
  /** Fade timer for the damage direction indicator (1.0 on hit → 0 over ~1.2s). */
  combatDmgDirFade: number;
  /** Last locked NPC id — for debouncing the lock-acquired audio cue. */
  lastLockId: string | null;
  /** Last observed waveIndex — for debouncing the wave-inbound audio cue. */
  lastWaveIndex: number;
  driftLog: string | null; // B-NARRATIVE: derelict log shown while dropped in
  nearestSignal: { signal: SignalSource; distanceKm: number } | null;
  // AAA-02/03: Verification Ledger rows (HUD) + ids already verified (prompt gate).
  ledger: LedgerRow[];
  verifiedIds: Set<string>;
  verificationModule: VerificationModule;
  // W1.1 (campaign wave 1): player death FSM. ALIVE → DRIFTING (controls dead,
  // hull tumbles) → CARD (VESSEL LOST overlay) → GRACE (post-restore 5s
  // invulnerability) → ALIVE.
  deathPhase: 'ALIVE' | 'DRIFTING' | 'CARD' | 'GRACE';
  deathT: number; // seconds in the current death phase
  deathOverlay: DeathOverlay;
  // CAMPAIGN W1: comms + chapter cards + the director (null = legacy mode when
  // the beat table fails validation — the build never loses the old opener).
  commsPanel: CommsPanel;
  chapterCard: ChapterCard;
  director: CampaignDirector | null;
  // N2: optional cloud-record keeper (Netlify Identity + Blobs mirror).
  cloud: CloudSaves;
}

const computeSpawn = (world: World): Spawn => {
  const earth = world.get('Earth');
  const earthPos = earth ? earth.worldPos : vec3();
  const earthVec = new THREE.Vector3(earthPos.x, earthPos.y, earthPos.z);
  const sunward = earthVec.clone().normalize(); // Sol -> Earth
  // Perpendicular-in-plane direction for a 3/4 lit view (not a dead-on full disc).
  const perp = new THREE.Vector3(-sunward.z, 0, sunward.x).normalize();
  // Offsets scale with the body radius so we sit WELL OUTSIDE the planet
  // (true-scale Earth is 6371 km; a fixed 5000 km put the camera inside it).
  const R = earth ? earth.entry.radiusKm : 6371;
  const spawnVec = earthVec
    .clone()
    .addScaledVector(sunward, -2.4 * R) // toward Sol -> camera sees the LIT side, well clear of the surface
    .addScaledVector(perp, 0.9 * R) // off-axis for a crescent/terminator
    .add(new THREE.Vector3(0, 0.5 * R, 0)); // slight elevation, Luna in frame
  const lookM = new THREE.Matrix4().lookAt(spawnVec, earthVec, new THREE.Vector3(0, 1, 0));
  const lookQ = new THREE.Quaternion().setFromRotationMatrix(lookM);
  return {
    pos: { x: spawnVec.x, y: spawnVec.y, z: spawnVec.z },
    orientation: { w: lookQ.w, x: lookQ.x, y: lookQ.y, z: lookQ.z },
  };
};

/**
 * M2 P3: overwrite each NPC's sim-core offset-space spawn with its true WORLD
 * lane point (host body + offset, resolved via the StationRenderer), preserving
 * the deterministic laneT fraction from makeTraffic.
 */
const reseedNpcsToLanes = (npcs: NpcShip[], stations: StationRenderer, world: World): void => {
  for (const n of npcs) {
    const o = stationById(n.originId);
    const d = stationById(n.destId);
    if (!o || !d) continue;
    const op = stations.worldPos(o, world);
    const dp = stations.worldPos(d, world);
    const t = n.laneT;
    n.pos = {
      x: op.x + (dp.x - op.x) * t,
      y: op.y + (dp.y - op.y) * t,
      z: op.z + (dp.z - op.z) * t,
    };
  }
};

/** M2 P3: build the deterministic NPC fleet + its renderer (world lanes resolved). */
const setupTraffic = async (
  stations: StationRenderer,
  world: World,
  parent: THREE.Group,
): Promise<{ npcs: NpcShip[]; npcRenderer: NpcRenderer }> => {
  const highGuard = STATIONS[0] as StationSpec;
  // SHIP-EXPANSION (faction territory read): ambient lanes fly the local region's
  // civilian hulls. Derive the region faction the same way the signal field does —
  // from the quantized world position of the hub station — so lane traffic matches
  // the pocket's ownership. Seeds (1337 / 7421) stay fixed so the fleet is
  // deterministic; `undefined` (no hub / unresolved) preserves the legacy lane mix.
  const hubPos = highGuard ? stations.worldPos(highGuard, world) : null;
  const faction: FactionId | undefined = hubPos ? regionFaction(positionSeed(hubPos)) : undefined;
  const npcs = makeTraffic(STATIONS, 8, 1337, faction);
  // M3 WORLD B4: a denser local cohort around High Guard so the port reads as
  // inhabited (5-10 ships visibly maneuvering near the busiest station).
  const cohort = makeStationTraffic(highGuard, 8, 7421, faction);
  for (const c of cohort) npcs.push(c);
  reseedNpcsToLanes(npcs, stations, world);
  const npcRenderer = new NpcRenderer(npcs, parent);
  await npcRenderer.loadAll();
  return { npcs, npcRenderer };
};

/** M2 P4: initial combat state — projectile pool, NPC HP map, lock/cooldown/Roll. */
const initCombat = (
  npcs: NpcShip[],
  parent: THREE.Group,
): {
  projectiles: Projectile[];
  projRenderer: ProjectileRenderer;
  combatFx: CombatFxRenderer;
  hp: Map<string, Combatant>;
  npcBodies: Map<string, RigidBody>;
  combatLock: CombatLock | null;
  fireCooldown: number;
  boltSeed: number;
  roll: { name: string; age: number }[];
  combatShakeImpulse: number;
  combatHitFlash: number;
  combatDmgFlash: number;
  combatDmgDirAngle: number;
  combatDmgDirFade: number;
  lastLockId: string | null;
  lastWaveIndex: number;
} => ({
  projectiles: [],
  projRenderer: new ProjectileRenderer(parent),
  combatFx: new CombatFxRenderer(parent),
  hp: new Map(
    npcs.map((n) => {
      const sp = shipById(n.shipType);
      return [n.id, makeCombatant(sp.hullMax, sp.shieldMax)] as const;
    }),
  ),
  npcBodies: new Map<string, RigidBody>(),
  combatLock: null,
  fireCooldown: 0,
  boltSeed: 0,
  roll: [],
  combatShakeImpulse: 0,
  combatHitFlash: 0,
  combatDmgFlash: 0,
  combatDmgDirAngle: 0,
  combatDmgDirFade: 0,
  lastLockId: null,
  lastWaveIndex: 0,
});

/** M2 P5/P7: wallet/cargo/missions — restored from a save when present. */
const initEconomy = (
  saved: SaveState | null,
): { wallet: Wallet; cargo: CargoHold; missions: Mission[]; activeMission: Mission | null } => {
  const missions = generateMissions(STATIONS, 5150);
  return {
    wallet: saved ? { credits: saved.credits } : initialWallet(),
    cargo: saved ? { items: saved.cargo, capacity: DEFAULT_CARGO_CAPACITY } : initialCargo(),
    missions,
    activeMission: saved ? (missions.find((m) => m.id === saved.activeMissionId) ?? null) : null,
  };
};

/**
 * M3 WORLD B6/B8: restore objectives from a v2 save, or seed the starter set
 * (opener + salvage + bounty) for a fresh game / migrated v1 save (empty list).
 */
const initObjectives = (
  saved: SaveState | null,
): { objectives: Objective[]; activeObjectiveId: string | null } => {
  const fresh = makeObjectives('story-cenotaph-vigil');
  if (!saved || saved.objectives.length === 0) {
    return { objectives: fresh, activeObjectiveId: fresh[0]?.id ?? null };
  }
  return { objectives: saved.objectives, activeObjectiveId: saved.activeObjectiveId };
};

/**
 * M2 P7 / M3 WORLD B8: capture the current Sim into a versioned (v2) SaveState.
 * Live signal POSITIONS are not saved — only discovered IDS — since the field is
 * re-seeded deterministically from the player's quantized position on load.
 */
const snapshot = (s: Sim): SaveState => ({
  version: SAVE_VERSION,
  shipId: s.ship.spec.id,
  credits: s.wallet.credits,
  cargo: s.cargo.items,
  activeMissionId: s.activeMission?.id ?? null,
  pos: s.ship.worldPos,
  orientation: s.ship.orientation,
  discoveredSignalIds: [...s.signalMgr.discovered],
  objectives: s.objectives,
  activeObjectiveId: s.activeObjectiveId,
  // AUDIT v3 (session 6): the carved record is permanent — persist it.
  verifiedIds: [...s.verifiedIds],
  ledger: s.ledger.map(({ name, status }) => ({ name, status })),
  // CAMPAIGN W1 (v4): the director's slice (null in legacy fallback mode).
  campaign: s.director ? s.director.saveSlice : null,
  // SHIP-EXPANSION (v5): persist the hangar (owned hulls + standing + unlocks).
  ownedShips: [...s.ownedShips],
  factionStanding: { ...s.factionStanding },
  shipUnlocks: [...s.shipUnlocks],
});

/** M2 P5: accessors letting the docked services menu mutate Sim-owned economy state. */
const makeEconomyBinding = (s: Sim): EconomyBinding => ({
  getWallet: () => s.wallet,
  setWallet: (w) => {
    s.wallet = w;
  },
  getCargo: () => s.cargo,
  setCargo: (c) => {
    s.cargo = c;
  },
  getMissions: () => s.missions,
  getActiveMission: () => s.activeMission,
  setActiveMission: (m) => {
    s.activeMission = m;
  },
  getObjectives: () => s.objectives, // M3 WORLD B6: Tasks tab feed
});

const buildSim = async (
  canvas: HTMLCanvasElement,
  saved: SaveState | null,
  audio: AudioEngine,
): Promise<Sim> => {
  const bundle = createScene(canvas);
  bundle.renderer.autoClear = false;
  const world = new World(await loadManifest());
  world.update(0);

  const bodyRenderer = new BodyRenderer(world, bundle.worldGroup);
  await bodyRenderer.loadAll();
  const stations = new StationRenderer(STATIONS, bundle.worldGroup);
  await stations.loadAll();
  const { npcs, npcRenderer } = await setupTraffic(stations, world, bundle.worldGroup);
  const belt = new AsteroidBelt(bundle.worldGroup);
  await loadSkybox(bundle.scene, bundle.renderer); // VFX3: Milky Way backdrop
  const starfield = await createStarfield(); // VFX2: real HYG constellations
  bundle.scene.add(starfield);
  bundle.scene.add(bundle.camera);

  const spawn = saved ? { pos: saved.pos, orientation: saved.orientation } : computeSpawn(world);
  // SHIP-EXPANSION (v5): new games own only Vesper-3 (the default hull). The
  // resolved active ship MUST be one the player owns — resolveOwnedShipId falls
  // back to the first owned hull when the requested id (save / ?ship=) isn't owned.
  const ownedShips = saved?.ownedShips ?? ['vesper3'];
  const requestedShipId =
    saved?.shipId ?? (new URLSearchParams(location.search).get('ship') ?? 'vesper3');
  const shipId = resolveOwnedShipId(requestedShipId, ownedShips);
  // Wave 3: the PERSISTED active loadout drives the player's effective ship.
  // applyLoadout folds the fitted modules into the hull (mass + handling +
  // capacities) and yields the weapons that combat fires + the mounts render —
  // so a heavier fit flies heavier and the fitted guns are the ones equipped.
  const baseSpec = shipById(shipId);
  const playerEffective = applyLoadout(baseSpec, getActive(), STARTER_MODULES);
  const ship = new ShipController(spawn.pos, spawn.orientation, playerEffective.spec);
  ship.assist = getSettings().gameplay.flightAssistDefault;
  ship.rotAssist = getSettings().gameplay.flightAssistDefault;
  const playerShip = await loadPlayerShip(baseSpec.id);
  bundle.worldGroup.add(playerShip.root);
  // Wave 2/3: mount the active loadout's fitted weapon meshes on the hardpoints.
  const weaponMounts = new WeaponMounts(playerShip, assetDir('weapons'));
  await weaponMounts.sync(playerEffective);
  const targets = new TargetManager(world);
  targets.select('Earth'); // POLISH 2: default target is Earth, not Sol

  const s: Sim = {
    world,
    bodyRenderer,
    belt,
    starfield,
    ship,
    input: new Input(canvas),
    camRig: new CameraRig(bundle.camera),
    targets,
    cruise: new Supercruise(),
    hud: new Hud(),
    radar3d: new Radar3D(),
    cockpit: new CockpitView(baseSpec.id),
    playerShip,
    baseSpec,
    effective: playerEffective,
    weaponMounts,
    overlay: new StartupOverlay(canvas),
    pauseMenu: new PauseMenu(),
    bundle,
    sunGlare: new SunGlare(bundle.scene),
    stations,
    svc: undefined as unknown as ServicesMenu, // set below (binding closes over s)
    targetMenu: new TargetMenu(),
    dock: initialDockingState(),
    npcs,
    npcRenderer,
    ...initCombat(npcs, bundle.worldGroup),
    ...initEconomy(saved),
    ownedShips,
    factionStanding: saved?.factionStanding ?? neutralFactionStanding(),
    shipUnlocks: saved?.shipUnlocks ?? [],
    audio,
    flightFeel: new FlightFeel(),
    speedLines: new SpeedLines(),
    supercruiseWarp: new SupercruiseWarp(),
    ...initWorldB(world, saved, bundle.worldGroup),
    // W1.1: player death FSM (VESSEL LOST → resume from last record).
    deathPhase: 'ALIVE',
    deathT: 0,
    deathOverlay: new DeathOverlay(),
    // CAMPAIGN W1: comms + cards now; director constructed below (closes over s).
    commsPanel: new CommsPanel(audio, () => getSettings().accessibility.voiceAutoAdvance),
    chapterCard: new ChapterCard(),
    director: null,
    cloud: new CloudSaves(),
  };
  // Wave-0 §2: bind the player's fired bolts to the active fitted weapon's visual
  // (per-weapon tracer geometry/streak), so different loadouts read distinctly.
  s.projRenderer.setPlayerWeapon(playerEffective.weapons[0]);
  s.hud.setMode(s.camRig.mode);
  s.cockpit.setMode(s.camRig.mode);
  bundle.scene.add(s.speedLines.object); // M3 FLIGHT-FEEL A6: speed-line streaks
  // FIX-WARP: parent under camera (NOT scene) so the tunnel inherits camera rotation —
  // the streak cone stays aligned to where the player is facing at all times.
  bundle.camera.add(s.supercruiseWarp.object);
  await s.signalRenderer.loadDerelict(); // B3: lazy GLB (box fallback) — non-blocking art
  s.svc = new ServicesMenu(ship, makeEconomyBinding(s), s.cloud);
  // ITEM 2 + CAMPAIGN W1: teach Input which predicates block pointer re-lock.
  s.input.setMenuOpenPredicate(
    () => s.targetMenu.isOpen || s.svc.isOpen || s.pauseMenu.isOpen || s.commsPanel.choicesVisible,
  );
  // CAMPAIGN W1: construct the director iff the beat table validates; on any
  // content error the build falls back to the legacy opener (never bricks).
  const beatErrors = validateBeatTable(CAMPAIGN_BEATS);
  if (beatErrors.length === 0) {
    // The campaign owns the opener objective; drop the legacy one.
    s.objectives = s.objectives.filter((o) => o.id !== 'obj-opening-verify');
    s.director = new CampaignDirector(CAMPAIGN_BEATS, saved?.campaign ?? null, saved !== null, {
      signalMgr: s.signalMgr,
      resolveAnchor: (a) => ('body' in a ? (s.world.get(a.body)?.worldPos ?? null) : a.absolute),
      pushObjective: (o) => {
        s.objectives = [...s.objectives.filter((x) => x.id !== o.id), o];
        s.activeObjectiveId = o.id;
      },
      // CAMPAIGN: a beat's marker hint locks its signal as the nav-target so the
      // HUD heading bracket + off-screen arrow point the player at it (the existing
      // signal nav-lock path the target menu / [N] already use).
      markObjectiveSignal: (sig) => s.targets.lockSignal(sig),
      rollEntry: (name) => {
        s.roll.unshift({ name, age: 0 });
        s.audio.death();
      },
      commsPanel: s.commsPanel,
      chapterCard: s.chapterCard,
      suppressStartup: () => s.overlay.suppress(),
      releaseStartup: () => s.overlay.release(),
    });
  } else {
    console.warn('[campaign] beat table invalid — legacy mode', beatErrors);
  }
  return s;
};

/**
 * M3 WORLD B2-B8: build the living-world subsystems — the SignalManager (seeded
 * off the player's quantized position; story signal anchored near Mars), the
 * signal + salvage renderers, and the objective set (restored or seeded).
 */
const initWorldB = (
  world: World,
  saved: SaveState | null,
  parent: THREE.Group,
): Pick<
  Sim,
  | 'signalMgr'
  | 'signalRenderer'
  | 'salvageRenderer'
  | 'objectives'
  | 'activeObjectiveId'
  | 'driftLog'
  | 'nearestSignal'
  | 'ledger'
  | 'verifiedIds'
  | 'verificationModule'
  | 'cenotaph'
  | 'orbitLines'
  | 'anomalyFx'
> => {
  const mars = world.get('Mars');
  const marsPos = mars ? mars.worldPos : vec3(0, 0, 0);
  const { objectives, activeObjectiveId } = initObjectives(saved);
  // AAA-13 debug: ?signal=derelict|distress|memorial|... forces the nearest ambient
  // signal's kind so the verify beat / combat pocket are reachable in seconds.
  const forced = new URLSearchParams(window.location.search).get('signal');
  const SIGNAL_KINDS: SignalKind[] = ['derelict', 'distress', 'wreckfield', 'ambush', 'mining', 'anomaly', 'memorial'];
  const forceFirstKind = SIGNAL_KINDS.includes(forced as SignalKind) ? (forced as SignalKind) : undefined;
  return {
    signalMgr: new SignalManager(marsPos, saved?.discoveredSignalIds ?? [], forceFirstKind),
    signalRenderer: new SignalRenderer(parent),
    salvageRenderer: new SalvageRenderer(parent),
    cenotaph: new CenotaphRenderer(parent),
    orbitLines: new OrbitLines(parent, world, STATIONS),
    anomalyFx: new AnomalyFxRenderer(parent),
    objectives,
    activeObjectiveId,
    driftLog: null,
    nearestSignal: null,
    // AUDIT v3 (session 6): restore the carved record — ledger rows come back
    // fully revealed (no replayed glyph animation for old verifies).
    ledger: (saved?.ledger ?? []).map((r) => ({ ...r, revealChars: r.name.length })),
    verifiedIds: new Set<string>(saved?.verifiedIds ?? []),
    verificationModule: new VerificationModule(),
  };
};

/** M2 P4: combat-lock HP + Roll fields for the HUD frame (kept out of buildHudFrame). */
const combatHudFields = (
  lock: CombatLock | null,
  hp: Map<string, Combatant>,
  roll: { name: string; age: number }[],
  hitFlash: number,
  dmgFlash: number,
): Pick<HudFrame, 'combatName' | 'combatHull01' | 'combatShield01' | 'combatDistanceKm' | 'combatHitFlash' | 'combatDmgFlash' | 'rollNotices'> => {
  const c = lock ? hp.get(lock.id) : undefined;
  return {
    combatName: lock ? npcName(lock.id) : null,
    combatHull01: c ? c.hull / c.hullMax : 0,
    combatShield01: c ? c.shield / c.shieldMax : 0,
    combatDistanceKm: lock ? lock.distanceKm : 0,
    combatHitFlash: hitFlash,
    combatDmgFlash: dmgFlash,
    rollNotices: roll,
  };
};

/**
 * H3: compute the four combat-readability fields for the HUD frame.
 * All projection is done here so hud.ts stays pure DOM/SVG with no Vec3 math.
 */
const combatReadabilityFields = (
  lock: CombatLock | null,
  hp: Map<string, Combatant>,
  npcs: readonly { id: string; pos: Vec3; vel: Vec3 }[],
  shipPos: Vec3,
  camera: THREE.Camera,
  dmgDirAngle: number,
  dmgDirFade: number,
  active: boolean, // signalMgr.active !== null
): Pick<HudFrame, 'combatLockScreen' | 'combatOffScreen' | 'combatLeadScreen' | 'combatDmgDir'> => {
  // 1. Lock health bar: project locked target, skip if behind/off-screen.
  let combatLockScreen: HudFrame['combatLockScreen'] = null;
  if (lock) {
    const c = hp.get(lock.id);
    const tmp = new THREE.Vector3(lock.pos.x - shipPos.x, lock.pos.y - shipPos.y, lock.pos.z - shipPos.z);
    const proj = Hud.project(tmp, camera);
    const w = window.innerWidth; const h = window.innerHeight;
    const onScr = !proj.behind && proj.x > 0 && proj.x < w && proj.y > 0 && proj.y < h;
    if (onScr) {
      combatLockScreen = {
        x: proj.x, y: proj.y,
        hull01: c ? c.hull / c.hullMax : 0,
        shield01: c ? c.shield / c.shieldMax : 0,
      };
    }
  }
  // 2. Off-screen hostile arrows: only during an active encounter.
  const combatOffScreen: HudFrame['combatOffScreen'] = [];
  if (active) {
    const w = window.innerWidth; const h = window.innerHeight;
    const margin = 40;
    for (const n of npcs) {
      if (!n.id.startsWith('enc-')) continue;
      if (combatOffScreen.length >= 5) break;
      const tmp2 = new THREE.Vector3(n.pos.x - shipPos.x, n.pos.y - shipPos.y, n.pos.z - shipPos.z);
      const p2 = Hud.project(tmp2, camera);
      const onScr2 = !p2.behind && p2.x > margin && p2.x < w - margin && p2.y > margin && p2.y < h - margin;
      if (!onScr2) {
        const cx = w / 2; const cy = h / 2;
        let dx = p2.x - cx; let dy = p2.y - cy;
        if (p2.behind) { dx = -dx; dy = -dy; }
        combatOffScreen.push({ angle: Math.atan2(dy, dx) });
      }
    }
  }
  // 3. Lead pip: project aimDirection intercept point.
  let combatLeadScreen: HudFrame['combatLeadScreen'] = null;
  if (lock) {
    const locked = npcs.find((n) => n.id === lock.id);
    if (locked) {
      const dir = aimDirection(shipPos, locked.pos, locked.vel, BOLT_SPEED_KMS);
      const LEAD_DIST = 1000;
      const tmp3 = new THREE.Vector3(dir.x * LEAD_DIST, dir.y * LEAD_DIST, dir.z * LEAD_DIST);
      const p3 = Hud.project(tmp3, camera);
      const w = window.innerWidth; const h = window.innerHeight;
      if (!p3.behind && p3.x > 0 && p3.x < w && p3.y > 0 && p3.y < h) {
        combatLeadScreen = { x: p3.x, y: p3.y };
      }
    }
  }
  // 4. Damage direction indicator.
  const combatDmgDir = dmgDirFade > 0 ? { angle: dmgDirAngle, fade: dmgDirFade } : null;
  return { combatLockScreen, combatOffScreen, combatLeadScreen, combatDmgDir };
};

/** M2 P5: economy HUD fields (spread into the frame to keep buildHudFrame lean). */
const econHudFields = (
  wallet: Wallet,
  cargo: CargoHold,
  active: Mission | null,
): Pick<HudFrame, 'credits' | 'cargoUsed' | 'cargoCapacity' | 'missionText'> => ({
  credits: wallet.credits,
  cargoUsed: cargoUsed(cargo),
  cargoCapacity: cargo.capacity,
  missionText: active
    ? `CONSIGNMENT: ${active.qty}× ${active.commodityId} → ${active.toStationId} (+${active.reward})`
    : null,
});

/**
 * M2 P2 / FIX-DOCKING 2: contextual one-line docking prompt for the HUD panel.
 * Reflects the new auto-dock UX: [G] DOCK when in range and eligible; once
 * cleared the auto-approach takes over and shows AUTO-DOCKING until captured.
 */
const dockPrompt = (phase: DockingPhase, ctx: StationContext | null): string => {
  if (!ctx) return '';
  const { kin } = ctx;
  switch (phase) {
    case 'IDLE':
      if (kin.speedKmS > REQUEST_MAX_SPEED_KMS) return 'REDUCE SPEED TO DOCK';
      if (kin.alignment < MIN_ALIGNMENT) return 'ALIGN WITH STATION';
      return '[G] DOCK';
    case 'REQUESTED':
      return 'REQUESTING…';
    case 'CLEARED':
    case 'APPROACH':
      return 'AUTO-DOCKING…';
    case 'DOCKED':
      return 'DOCKED — [G] UNDOCK';
    case 'UNDOCKING':
      return 'UNDOCKING…';
    default:
      return '';
  }
};

/** Project a render-space point (ship-relative) to screen, reusing tmpVec. */
const projectDelta = (
  tmp: THREE.Vector3,
  wp: Vec3,
  shipPos: Vec3,
  camera: THREE.Camera,
): HudFrame['targetScreen'] => {
  tmp.set(wp.x - shipPos.x, wp.y - shipPos.y, wp.z - shipPos.z);
  return Hud.project(tmp, camera);
};

type BootAction = 'continue' | 'new';

const readBootSummary = (saved: SaveState | null): MenuSaveSummary => {
  const last = saved ? saved.ledger[saved.ledger.length - 1] : null;
  return {
    hasSave: saved !== null,
    shipName: saved ? shipById(saved.shipId).name : null,
    lastVerifiedName: last?.name ?? null,
    namesCarved: saved?.ledger.length ?? 0,
  };
};

const chooseBootAction = (
  summary: MenuSaveSummary,
  settingsScreen: SettingsScreen,
  /** True while the intro's live engine frame persists under the title (no
   *  static-plate pop, no second glyph carve — Wave 1 handoff parity). */
  liveUnderlay = false,
): Promise<BootAction> =>
  new Promise((resolve) => {
    const title = new TitleScreen();
    const menu = new MainMenu();
    const credits = new CreditsScreen();
    const finish = (choice: BootAction): void => {
      settingsScreen.hide();
      title.dispose();
      menu.dispose();
      credits.dispose();
      resolve(choice);
    };
    const showMenu = (): void => {
      menu.show(summary, {
        continueRecord: () => finish('continue'),
        newRecord: () => finish('new'),
        openSettings: () => {
          menu.hide();
          settingsScreen.show('display', showMenu);
        },
        openHangar: () => window.open('/ships.html', '_blank', 'noopener'),
        openAudio: () => window.open('/audio.html', '_blank', 'noopener'),
        openCredits: () => {
          menu.hide();
          credits.show(showMenu);
        },
        quit: () => {
          menu.hide();
          title.show(showMenu, liveUnderlay);
        },
      });
    };
    title.show(showMenu, liveUnderlay);
  });

const encounterHasHostiles = (s: Sim): boolean =>
  s.combatLock !== null ||
  s.npcs.some((n) => n.id.startsWith('enc-') && s.hp.has(n.id));

const nearCenotaph = (s: Sim): boolean => {
  const active = s.signalMgr.active?.signal.id;
  if (active === 'story-cenotaph-vigil') return true;
  const signal = s.signalMgr.signals.find((sg) => sg.id === 'story-cenotaph-vigil');
  return !!signal && distance(s.ship.worldPos, signal.pos) <= CENOTAPH_MUSIC_RANGE_KM;
};

const musicStateForSim = (
  s: Sim,
  settingsScreen: SettingsScreen,
): { context: ReturnType<typeof selectMusicContext>; ducked: boolean } => ({
  context: selectMusicContext({
    // Combat-exit hysteresis in MusicManager holds tension→flight; the pause menu
    // also counts as a menu snapshot so a paused fight drops to the menu bed.
    menuOpen: settingsScreen.isOpen || s.pauseMenu.isOpen,
    docked: s.dock.phase === 'DOCKED',
    nearCenotaph: nearCenotaph(s),
    verifying: s.camRig.verifying || s.verificationModule.visible,
    combatActive: encounterHasHostiles(s),
  }),
  ducked: s.commsPanel.isOpen || s.verificationModule.visible,
});

const start = async (): Promise<void> => {
  const saved = loadGame();
  const settingsScreen = new SettingsScreen();
  const audio = new AudioEngine();
  const music = new MusicManager(audio);
  audio.applySettings(getSettings().audio);
  const unbindBootAudio = settingsStore.subscribe((settings) => audio.applySettings(settings.audio));
  // Black Marble cinematic intro on EVERY cold boot / return-to-title (no
  // sessionStorage gate, always skippable). `?intro=skip` bypasses it; any other
  // value (e.g. `?intro=force`) plays it. The eager menu-music bring-up is
  // deferred until AFTER the intro so the score owns the music bus while it runs.
  let introUnderlay: IntroCinematic | null = null;
  if (shouldPlayIntro(location.search)) {
    const intro = new IntroCinematic();
    const introAudio = new IntroAudio(audio);
    introAudio.arm();
    // Wave 1: cues start off the CineClock (offset-into-buffer on late decode),
    // never off wall-clock at gesture time.
    intro.tickListener = (t) => introAudio.sync(t);
    await intro.play();
    introAudio.crossfadeToMenu(music);
    introAudio.dispose();
    // Wave 1 handoff parity: the cinematic's live engine frame STAYS under the
    // TitleScreen (marble still breathing) instead of popping to a static PNG;
    // it is torn down only once the player commits a boot action.
    intro.beginTitleUnderlay();
    introUnderlay = intro;
  }
  music.setState({ context: 'menu', ducked: false });
  const unlock = (): void => music.resume();
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
  const boot = await chooseBootAction(readBootSummary(saved), settingsScreen, introUnderlay !== null);
  introUnderlay?.dispose();
  let sessionRecord = boot === 'continue' ? saved : null;
  const canvas = document.getElementById('app') as HTMLCanvasElement;
  const s = await buildSim(canvas, sessionRecord, audio);
  bindRuntimeSettings({
    audio: s.audio,
    overlay: s.overlay,
    scene: s.bundle,
    ship: s.ship,
  });
  // Wave 3: re-apply the active loadout live whenever it changes (ship-builder
  // UI / loadoutStore.setActive). Rebuilds the effective ship from the unchanged
  // base hull, re-specs the flight controller (handling + capacities), re-points
  // the bolt visual, and re-mounts the fitted weapon meshes.
  const applyActiveLoadout = (loadout: Loadout): void => {
    s.effective = applyLoadout(s.baseSpec, loadout, STARTER_MODULES);
    s.ship.setSpec(s.effective.spec);
    s.projRenderer.setPlayerWeapon(s.effective.weapons[0]);
    void s.weaponMounts.setLoadout(loadout);
  };
  const unbindLoadout = loadoutStore.subscribe(applyActiveLoadout);
  window.addEventListener('beforeunload', unbindLoadout);
  unbindBootAudio();
  music.setState(musicStateForSim(s, settingsScreen));
  s.input.setMenuOpenPredicate(
    () =>
      settingsScreen.isOpen ||
      s.targetMenu.isOpen ||
      s.svc.isOpen ||
      s.pauseMenu.isOpen ||
      s.commsPanel.choicesVisible,
  );
  // M4 CONTROL-FEEL: ?tune=1 live feel-tuning panel (stick + shared camera/feel state).
  initTunePanel(buildTuneGroups(s.ship, s.camRig, s.flightFeel));
  // M2 P7: autosave periodically + on tab close. W1.1: GATED on being alive —
  // the LAST RECORD must never be the place the vessel died.
  // N2: every local persist also stamps the write time and mirrors to the
  // cloud keeper (debounced; offline-first — failures never block play).
  const persist = (): void => {
      const snap = snapshot(s);
      sessionRecord = snap;
    saveGame(snap);
    CloudSaves.stampLocal();
    s.cloud.push(JSON.stringify(snap));
  };
  setInterval(() => { if (s.deathPhase === 'ALIVE') persist(); }, 10_000);
  window.addEventListener('beforeunload', () => {
    if (s.deathPhase !== 'ALIVE') return;
    persist();
    void s.cloud.flush(); // best-effort immediate mirror on the way out
  });
  // N2: boot-time pull — if the kept record is newer than the local write,
  // offer the restore (newest-wins WITH consent), then reboot from it.
  if (s.cloud.signedIn) {
    void s.cloud.pull().then(async (kept) => {
      if (!kept || !kept.updatedAt) return;
      const localTs = CloudSaves.localStamp();
      if (localTs && localTs >= kept.updatedAt) return;
      if (await offerNewerRecord(kept.updatedAt)) {
        try {
          localStorage.setItem('blackmarble.save.v1', JSON.stringify(kept.save));
          CloudSaves.stampLocal();
          location.reload();
        } catch {
          /* storage unavailable — keep playing local */
        }
      }
    });
  }
  const debugMode = new URLSearchParams(window.location.search).has('debug');
  const tmpVec = new THREE.Vector3();
  let lastNow = performance.now();
  let lastPresented = 0;
  let simTime = 0;
  const clampRadarAxis = (delta: number, rangeKm: number): number =>
    Math.max(-1, Math.min(1, delta / Math.max(1, rangeKm)));

  const radarRangeKm = (shipPos: Vec3): number => {
    const hostiles = s.npcs.filter((n) => n.id.startsWith('enc-') && !isVictim(n.id));
    if (hostiles.length === 0) return RADAR_RANGE_DEFAULT;
    const farthest = Math.max(...hostiles.map((n) => distance(n.pos, shipPos)));
    return Math.max(RADAR_RANGE_COMBAT, Math.min(RADAR_RANGE_DEFAULT, farthest * 1.2));
  };

  const radarBlipFromWorld = (
    worldPos: Vec3,
    shipPos: Vec3,
    qConj: Quat,
    rangeKm: number,
    kind: HudFrame['radarBlips'][number]['kind'],
    selected: boolean,
    threat?: number,
  ): HudFrame['radarBlips'][number] => {
    const body = rotateVec3(qConj, sub(worldPos, shipPos));
    const blip: HudFrame['radarBlips'][number] = {
      x: clampRadarAxis(body.x, rangeKm),
      y: clampRadarAxis(body.y, rangeKm),
      z: clampRadarAxis(body.z, rangeKm),
      kind,
      selected,
    };
    if (threat !== undefined) blip.threat = threat;
    return blip;
  };

  const updateRadarBlips = (shipPos: Vec3): HudFrame['radarBlips'] => {
    const sel = s.targets.currentName;
    const qConj = conjugate(s.ship.orientation);
    const rangeKm = radarRangeKm(shipPos);
    return [
      ...s.world.bodies.map((b) =>
        radarBlipFromWorld(b.worldPos, shipPos, qConj, rangeKm, 'body', b.entry.name === sel),
      ),
      ...s.npcs.map((n) =>
        radarBlipFromWorld(
          n.pos,
          shipPos,
          qConj,
          rangeKm,
          n.id.startsWith('enc-') && !isVictim(n.id) ? 'hostile' : 'neutral',
          n.id === s.combatLock?.id || npcName(n.id) === sel,
        ),
      ),
      ...signalBlips(shipPos, qConj, rangeKm, sel),
    ];
  };

  /** M3: radar blips for live signal sources — now body-frame and height-aware. */
  const signalBlips = (shipPos: Vec3, qConj: Quat, rangeKm: number, sel: string): HudFrame['radarBlips'] =>
    s.signalMgr.signals.map((sig) =>
      radarBlipFromWorld(sig.pos, shipPos, qConj, rangeKm, 'signal', sig.title === sel, sig.threatLevel),
    );

  /**
   * M3 WORLD B6: dispatch one objective event to every objective; on a fresh
   * COMPLETE, pay the reward via the existing economy credit() and advance the
   * active-objective pointer to the next still-open task. Sombre — no fanfare.
   */
  const emitObjective = (ev: ObjectiveEvent): void => {
    s.objectives = s.objectives.map((o) => {
      const next = stepObjective(o, ev);
      if (next.status === 'COMPLETE' && o.status === 'ACTIVE') {
        s.wallet = credit(s.wallet, next.reward); // pay out
      }
      return next;
    });
    if (!s.objectives.find((o) => o.id === s.activeObjectiveId && o.status === 'ACTIVE')) {
      s.activeObjectiveId = s.objectives.find((o) => o.status === 'ACTIVE')?.id ?? null;
    }
    // CAMPAIGN W1: every objective event also feeds the campaign reducer.
    s.director?.onEvent(ev);
  };

  /** B2: leave the current encounter — despawn enc-* meshes + clear the drift-log. */
  const leaveEncounter = (): void => {
    for (const id of [...s.hp.keys()]) if (id.startsWith('enc-')) s.npcRenderer.remove(id);
    for (const id of [...s.npcBodies.keys()]) if (id.startsWith('enc-')) s.npcBodies.delete(id);
    s.npcs = s.signalMgr.clearEncounter(s.npcs, s.hp);
    s.driftLog = null;
    s.camRig.cancelVerify(); // AAA-03: drop the verify beat when leaving the pocket
    s.verificationModule.dismiss();
    // CAMPAIGN W1: clear the once-only ENCOUNTER_CLEARED latch with the pocket —
    // Act 2+ data may legitimately reuse a signal id across separate encounters.
    lastClearedSignalId = null;
  };

  // ── W1.1: player death FSM ──────────────────────────────────────────────────
  /** Seconds of dead-stick drift before the VESSEL LOST card fades in. */
  const DEATH_DRIFT_SEC = 2.8;
  /** Post-restore invulnerability (resolveHit skips ship damage while not ALIVE). */
  const RESPAWN_GRACE_SEC = 5;
  /** A fully-zeroed input snapshot — the dead ship answers no one. */
  const zeroInput = (): InputState => ({
    throttle: 0, strafe: 0, vertical: 0, roll: 0, keyYaw: 0, keyPitch: 0,
    mouseDX: 0, mouseDY: 0, toggleAssist: false, toggleRotAssist: false,
    boost: false, pipSys: false, pipEng: false, pipWep: false, toggleCamera: false,
    targetNext: false, targetPrev: false, toggleTargetMenu: false, targetSignal: false,
    toggleSupercruise: false, toggleLegend: false, dockRequest: false, fire: false,
    scoop: false, verify: false, toggleOrbitLines: false, lookReleased: false,
  });

  /** Detect hull 0 and enter the death sequence (sombre: drift, Roll, low tone). */
  const checkDeath = (): void => {
    if (s.deathPhase !== 'ALIVE') return;
    if (s.ship.state.hull > 0 || s.dock.phase === 'DOCKED') return;
    s.deathPhase = 'DRIFTING';
    s.deathT = 0;
    if (s.cruise.active) s.cruise.disengage();
    s.ship.resetStick();
    s.input.zeroThrottle();
    s.camRig.cancelVerify();
    s.verificationModule.dismiss();
    s.svc.hide();
    s.targetMenu.hide();
    document.exitPointerLock();
    // The Roll names the lost vessel quietly — same register as every other loss.
    s.roll.unshift({ name: s.ship.spec.name.toUpperCase(), age: 0 });
    s.audio.death();
    s.audio.deathDriftBed(); // dead-stick drift bed layers under the 130 Hz tone
  };

  /** Advance the death phases: drift → card; grace countdown → alive. */
  const stepDeath = (dt: number): void => {
    if (s.deathPhase === 'DRIFTING') {
      s.deathT += dt;
      if (s.deathT >= DEATH_DRIFT_SEC) {
        s.deathPhase = 'CARD';
        s.deathOverlay.show(s.ship.spec.name, restoreFromRecord);
      }
    } else if (s.deathPhase === 'GRACE') {
      s.deathT += dt;
      if (s.deathT >= RESPAWN_GRACE_SEC) s.deathPhase = 'ALIVE';
    }
  };

  /** Resume from the last record: in-place restore from the save (no reload
   * when clean; any throw falls back to a full reload, whose boot path reads
   * the same record). */
  const restoreFromRecord = (): void => {
    try {
      if (!sessionRecord) {
        location.reload();
        return;
      }
      const saved = sessionRecord;
      leaveEncounter();
      s.projectiles = [];
      s.combatLock = null;
      s.combatHitFlash = 0;
      s.combatDmgFlash = 0;
      s.combatDmgDirFade = 0;
      npcFireCd.clear();
      s.ship.restorePose(saved.pos, saved.orientation);
      s.ship.resetState();
      s.camRig.snap();
      s.wallet = { credits: saved.credits };
      s.cargo = { items: saved.cargo, capacity: DEFAULT_CARGO_CAPACITY };
      s.activeMission = s.missions.find((m) => m.id === saved.activeMissionId) ?? null;
      if (saved.objectives.length > 0) {
        s.objectives = saved.objectives;
        s.activeObjectiveId = saved.activeObjectiveId;
      }
      s.verifiedIds = new Set(saved.verifiedIds);
      s.ledger = saved.ledger.map((r) => ({ ...r, revealChars: r.name.length }));
      // CAMPAIGN W1: rewind the director's slice (act/beat/flags) to the snapshot,
      // exactly as the full-reload boot path does — otherwise a death after a beat
      // advance since the last save leaves the campaign skewed against the record.
      s.director?.restore(saved.campaign ?? null);
      s.dock = initialDockingState();
      s.deathPhase = 'GRACE';
      s.deathT = 0;
    } catch {
      location.reload();
    }
  };

  const canPause = (): boolean =>
    s.deathPhase === 'ALIVE' &&
    !settingsScreen.isOpen &&
    !s.overlay.isVisible &&
    s.dock.phase !== 'DOCKED' &&
    !s.targetMenu.isOpen &&
    !s.svc.isOpen &&
    !s.chapterCard.isOpen &&
    !(s.director?.holdSim ?? false) &&
    !s.commsPanel.isOpen;

  const pauseHandlers = {
    resume: (): void => {
      s.audio.uiBack();
      s.pauseMenu.hide();
    },
    openAccessibility: (): void => {
      s.pauseMenu.hide();
      s.hud.setVisible(false);
      settingsScreen.show('accessibility', () => {
        s.hud.setVisible(true);
        s.pauseMenu.show(pauseHandlers);
      });
    },
    openSettings: (): void => {
      s.pauseMenu.hide();
      s.hud.setVisible(false);
      settingsScreen.show('display', () => {
        s.hud.setVisible(true);
        s.pauseMenu.show(pauseHandlers);
      });
    },
    returnToTitle: (): void => location.reload(),
  };

  const openPauseOnEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || s.pauseMenu.isOpen) return;
    if (!canPause()) {
      s.audio.uiDenied(); // Escape can't pause right now (comms / card / dead)
      return;
    }
    event.preventDefault();
    s.ship.resetStick();
    document.exitPointerLock();
    s.audio.uiConfirm();
    s.pauseMenu.show(pauseHandlers);
  };
  window.addEventListener('keydown', openPauseOnEscape);

  /**
   * M3 WORLD B2: drop into a signal source if one is in range while cruising;
   * otherwise the normal surface drop. Injects enc-* hostiles/victims into the
   * existing npcs+hp, shows the B-NARRATIVE drift-log, and fires the investigate
   * objective event (DROPPED_AT_SIGNAL).
   */
  const dropOutOfCruise = (): void => {
    const near = s.nearestSignal;
    // Compute the drop decision BEFORE dropFromSupercruise() zeroes velocity, so
    // the closing-speed lead still widens the band on a fast approach.
    const closing = near ? closingSpeedTo(s.ship.worldPos, s.ship.velocity, near.signal.pos) : 0;
    const dropIntoSignal =
      near !== null && shouldDropIntoSignal(near.distanceKm, near.signal.radiusKm, closing);
    s.ship.dropFromSupercruise();
    // Uses the cruise-state-INDEPENDENT predicate: the drop must execute regardless
    // of whether cruise has already toggled to NORMAL (structurally immune to the
    // toggle-ordering bug). The active-guard lives only on the HUD prompt caller.
    if (near && dropIntoSignal) {
      const enc = s.signalMgr.dropInto(near.signal, s.npcs, s.hp);
      for (const n of s.npcs) if (n.id.startsWith('enc-')) void s.npcRenderer.add(n);
      s.driftLog = enc.contents.driftLog ?? enc.contents.derelictTitle;
      // FIX: drop at the player's ACTUAL position — do NOT snap to a fixed hold
      // point. The old orbitHold() yanked the ship out to ~radius*0.02, so however
      // close you cruised you were teleported far from where you dropped.
      emitObjective({ type: 'DROPPED_AT_SIGNAL', signalId: near.signal.id });
      s.audio.dock(); // quiet arrival cue (reused; not triumphant)
      return;
    }
    // Body/destination drop: STAY where you dropped (keep your actual approach
    // distance). The per-frame collide() already keeps the ship above the surface
    // floor (1.01x radius), so no orbitHold reposition is needed — the old code
    // bounced you out to ~1.3x radius, which is very far for large planets.
  };

  /** AAA-02: ledger glyph-reveal rate (~45ms/char). */
  const LEDGER_CHARS_PER_SEC = 22;

  /** AAA-02/03: is the [V] VERIFY verb available now? True when dropped into a
   * verifiable signal (memorial/derelict/anomaly) not yet verified this session. */
  const verifyAvailable = (): boolean => {
    const active = s.signalMgr.active;
    return (
      !!active &&
      !s.verifiedIds.has(active.signal.id) &&
      verifySignal(active.signal).status !== 'PENDING'
    );
  };

  /** AAA-02/03: perform the verify — append the carved name to the Verification
   * Ledger, fire the VERIFIED objective event, and ease the camera onto the name. */
  const performVerify = (): void => {
    const active = s.signalMgr.active;
    if (!active || s.verifiedIds.has(active.signal.id)) return;
    const r = verifySignal(active.signal);
    if (r.status === 'PENDING') return;
    s.verificationModule.show(buildVerificationModuleView(active.signal, r), {
      dismissText: `DISMISS [${bindingLabel('verify')}]`,
      reduceMotion: getSettings().accessibility.reduceMotion,
    });
    s.verifiedIds.add(active.signal.id);
    const revealChars = getSettings().accessibility.reduceMotion ? r.name.length : 0;
    s.ledger.push({ name: r.name, status: r.status, revealChars });
    emitObjective({ type: 'VERIFIED', signalId: active.signal.id });
    const d = signalMarkerPos(active.signal);
    const p = s.ship.worldPos;
    s.camRig.enterVerify(
      new THREE.Vector3(d.x - p.x, d.y - p.y, d.z - p.z),
      getSettings().gameplay.verifyBeatSec,
    );
    s.ship.resetStick(); // M4: residual deflection must not rotate the hull under the beat
    if (r.status === 'CONTRADICTION') s.audio.recordContradiction();
    else s.audio.recordTruth();
  };

  const liveCameraPose = (): {
    mode: string;
    verifying: boolean;
    pos: { x: number; y: number; z: number };
    quat: { x: number; y: number; z: number; w: number };
  } => ({
    mode: s.camRig.mode,
    verifying: s.camRig.verifying,
    pos: {
      x: s.bundle.camera.position.x,
      y: s.bundle.camera.position.y,
      z: s.bundle.camera.position.z,
    },
    quat: {
      x: s.bundle.camera.quaternion.x,
      y: s.bundle.camera.quaternion.y,
      z: s.bundle.camera.quaternion.z,
      w: s.bundle.camera.quaternion.w,
    },
  });

  const quatAngle = (a: { x: number; y: number; z: number; w: number }, b: { x: number; y: number; z: number; w: number }): number =>
    2 * Math.acos(Math.min(1, Math.abs(a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w)));

  const waitMs = (ms: number): Promise<void> =>
    new Promise((resolve) => window.setTimeout(resolve, ms));

  const debugTurnProbe = (): { shipTurnRad: number; cameraTurnRad: number; cameraPosDeltaKm: number } => {
    const cam = new THREE.PerspectiveCamera();
    const rig = new CameraRig(cam);
    Object.assign(rig.tuning, s.camRig.tuning);
    const start = new THREE.Quaternion(
      s.ship.orientation.x,
      s.ship.orientation.y,
      s.ship.orientation.z,
      s.ship.orientation.w,
    );
    const target = start.clone().multiply(new THREE.Quaternion().setFromAxisAngle(Y_AXIS, 0.45));
    rig.update({ x: start.x, y: start.y, z: start.z, w: start.w }, 0);
    const p0 = cam.position.clone();
    const q0 = cam.quaternion.clone();
    for (let i = 0; i < 24; i += 1) rig.update({ x: target.x, y: target.y, z: target.z, w: target.w }, 1 / 60);
    return {
      shipTurnRad: start.angleTo(target),
      cameraTurnRad: q0.angleTo(cam.quaternion),
      cameraPosDeltaKm: p0.distanceTo(cam.position),
    };
  };

  const runCameraSmoke = async (): Promise<{
    verifyBeat: {
      availableBefore: boolean;
      ledgerAdvanced: boolean;
      cameraMovedRad: number;
      shipMovedRad: number;
      camVsShipRad: number;
    };
    orbitTurn: { shipTurnRad: number; cameraTurnRad: number; cameraPosDeltaKm: number };
  }> => {
    const sg = s.signalMgr.signals.find((x) => x.id === 'story-cenotaph-vigil');
    if (!sg) throw new Error('no story signal');
    s.cruise.disengage();
    s.ship.teleportLookAt(
      { x: sg.pos.x + 180, y: sg.pos.y + 80, z: sg.pos.z + 80 },
      signalMarkerPos(sg),
    );
    s.camRig.snap();
    s.signalMgr.dropInto(sg, s.npcs, s.hp);
    s.verifiedIds.delete(sg.id);
    const before = {
      cam: liveCameraPose(),
      ship: { ...s.ship.orientation },
      verify: verifyAvailable(),
      ledger: s.ledger.length,
    };
    performVerify();
    await waitMs(1400);
    const during = {
      cam: liveCameraPose(),
      ship: { ...s.ship.orientation },
      ledger: s.ledger.length,
    };
    s.camRig.cancelVerify();
    return {
      verifyBeat: {
        availableBefore: before.verify,
        ledgerAdvanced: during.ledger === before.ledger + 1,
        cameraMovedRad: quatAngle(before.cam.quat, during.cam.quat),
        shipMovedRad: quatAngle(before.ship, during.ship),
        camVsShipRad: quatAngle(during.cam.quat, during.ship),
      },
      orbitTurn: debugTurnProbe(),
    };
  };

  /** AAA-02: advance each ledger row's glyph reveal (client dt-driven, not sim-core). */
  const advanceLedger = (dt: number): void => {
    if (getSettings().accessibility.reduceMotion) {
      for (const e of s.ledger) e.revealChars = e.name.length;
      return;
    }
    for (const e of s.ledger) {
      if (e.revealChars < e.name.length) {
        e.revealChars = Math.min(e.name.length, e.revealChars + dt * LEDGER_CHARS_PER_SEC);
      }
    }
  };

  // AAA-13 debug (gated on ?debug): expose deterministic drop+verify hooks so the
  // Cenotaph / combat pockets can be exercised without manual 6DOF flight.
  if (new URLSearchParams(window.location.search).has('debug')) {
    (window as unknown as { __bm?: unknown }).__bm = {
      dropStory: (): string => {
        const sg = s.signalMgr.signals.find((x) => x.id === 'story-cenotaph-vigil');
        if (!sg) return 'no story signal';
        s.signalMgr.dropInto(sg, s.npcs, s.hp);
        s.driftLog = '(debug) dropped at the Cenotaph waypoint';
        return `dropped: ${sg.title}`;
      },
      verify: (): void => performVerify(),
      ledger: (): unknown => s.ledger,
      verifyAvailable: (): boolean => verifyAvailable(),
      cameraPose: (): unknown => liveCameraPose(),
      shipPose: (): unknown => ({ pos: s.ship.worldPos, quat: s.ship.orientation }),
      cameraSmoke: (): Promise<unknown> => runCameraSmoke(),
      gotoCenotaph: (): string => {
        const sg = s.signalMgr.signals.find((x) => x.id === 'story-cenotaph-vigil');
        if (!sg) return 'no story signal';
        s.cruise.disengage();
        const marker = signalMarkerPos(sg);
        // Frame the ship 200 km ahead-and-above the monument, looking at the authored panel marker.
        const arrivePos = { x: sg.pos.x + 180, y: sg.pos.y + 80, z: sg.pos.z + 80 };
        s.ship.teleportLookAt(arrivePos, marker);
        s.camRig.snap(); // M4: don't slow-swing the camera spring through a teleport
        return `framed at the Cenotaph (${sg.pos.x.toFixed(0)},${sg.pos.y.toFixed(0)},${sg.pos.z.toFixed(0)})`;
      },
      // AUDIT hooks (session 6): drop straight into a hostile pocket, face the
      // nearest hostile, and introspect combat state — so the wired-but-
      // unexercised combat/rescue flows can be exercised without manual 6DOF
      // flight. Client-only, ?debug-gated, replay-safe (same family as above).
      dropHostile: (kind = 'ambush'): string => {
        const sg = s.signalMgr.signals.find((x) => x.kind === kind);
        if (!sg) return `no ${kind} signal`;
        s.cruise.disengage();
        s.ship.teleportLookAt({ x: sg.pos.x + 60, y: sg.pos.y + 20, z: sg.pos.z + 20 }, sg.pos);
        s.camRig.snap();
        const enc = s.signalMgr.dropInto(sg, s.npcs, s.hp);
        for (const n of s.npcs) if (n.id.startsWith('enc-')) void s.npcRenderer.add(n);
        s.driftLog = enc.contents.driftLog ?? enc.contents.derelictTitle;
        emitObjective({ type: 'DROPPED_AT_SIGNAL', signalId: sg.id });
        return `dropped at ${sg.kind}: ${sg.title} (${enc.waves.length} wave(s))`;
      },
      faceHostile: (): string => {
        const hs = s.npcs.filter((n) => n.id.startsWith('enc-') && !isVictim(n.id));
        if (hs.length === 0) return 'no hostiles';
        const near = hs.reduce((a, b) =>
          distance(a.pos, s.ship.worldPos) <= distance(b.pos, s.ship.worldPos) ? a : b,
        );
        s.ship.teleportLookAt(s.ship.worldPos, near.pos);
        s.camRig.snap();
        return `facing ${near.id} @ ${Math.round(distance(near.pos, s.ship.worldPos))} km`;
      },
      // Audit: teleport to ~30 km off a station (default: first spec) so the
      // docking + services flows are reachable without a supercruise leg.
      gotoStation: (id?: string): string => {
        const spec = id ? STATIONS.find((st) => st.id === id) : STATIONS[0];
        if (!spec) return `no station ${id ?? ''}`;
        s.cruise.disengage();
        const wp = s.stations.worldPos(spec, s.world);
        // 6 km off — inside every station's dockingRadius so G can dock at once.
        s.ship.teleportLookAt({ x: wp.x + 5, y: wp.y + 2.5, z: wp.z + 2 }, wp);
        s.camRig.snap();
        return `at ${spec.name} (${spec.id})`;
      },
      // CAMPAIGN W1: introspect / drive the campaign.
      campaign: (): unknown => s.director?.current ?? null,
      gotoBeat: (id: string): string => s.director?.gotoBeat(id) ?? 'no director',
      // W1.1: drive the death sequence on demand (massive overkill damage
      // through the REAL takeDamage path).
      kill: (): string => {
        s.ship.takeDamage(1e9);
        return `hull ${s.ship.state.hull}`;
      },
      // Audit: grant credits so paywalled flows (mission cargo, turn-in) are
      // verifiable without an hour of honest trading.
      credit: (amount = 5000): string => {
        s.wallet = credit(s.wallet, Math.max(0, amount));
        return `ledger ${s.wallet.credits}`;
      },
      // Audit: kill all living hostiles through the REAL damage path
      // (applyDamage -> isDead -> killNpc), so downstream flows (Roll,
      // KILLED_NPC, wave advance, rescue resolution) run exactly as if the
      // player's bolts landed — without minutes of TTK grind.
      smite: (): string => {
        const targets = s.npcs.filter((n) => n.id.startsWith('enc-') && !isVictim(n.id));
        for (const n of targets) {
          const hp = s.hp.get(n.id);
          if (!hp || isDead(hp)) continue;
          const dead = applyDamage(hp, hp.shield + hp.hull + 1);
          s.hp.set(n.id, dead);
          if (isDead(dead)) killNpc(n);
        }
        return `smote ${targets.length} hostile(s)`;
      },
      combat: (): unknown => ({
        wave: s.signalMgr.active ? `${s.signalMgr.active.waveIndex}/${s.signalMgr.active.waves.length}` : null,
        // Audit: encounter-ref kind vs the live array copy (aliasing probe).
        encKind: s.signalMgr.active?.signal.kind ?? null,
        arrayKind: s.signalMgr.active
          ? s.signalMgr.signals.find((x) => x.id === s.signalMgr.active!.signal.id)?.kind ?? 'GONE'
          : null,
        hostiles: s.npcs
          .filter((n) => n.id.startsWith('enc-'))
          .map((n) => ({
            id: n.id,
            victim: isVictim(n.id),
            distKm: Math.round(distance(n.pos, s.ship.worldPos)),
            hp: s.hp.get(n.id) ?? null,
            // Audit: physics facing (identity = orientation write-back broken)
            // + whether the nose points at the player (return-fire aim gate).
            ori: `${n.orientation.x.toFixed(2)},${n.orientation.y.toFixed(2)},${n.orientation.z.toFixed(2)},${n.orientation.w.toFixed(2)}`,
            aimDot: (() => {
              const f = rotateVec3(n.orientation, vec3(0, 0, -1));
              const tx = s.ship.worldPos.x - n.pos.x;
              const ty = s.ship.worldPos.y - n.pos.y;
              const tz = s.ship.worldPos.z - n.pos.z;
              const tl = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
              return +((f.x * tx + f.y * ty + f.z * tz) / tl).toFixed(3);
            })(),
          })),
        projectiles: s.projectiles.reduce(
          (acc: Record<string, number>, p) => {
            const k = p.ownerId && p.ownerId.startsWith('enc-') ? 'npc' : 'player';
            acc[k] = (acc[k] ?? 0) + 1;
            return acc;
          },
          {},
        ),
        lock: s.combatLock?.id ?? null,
        shipHull: s.ship.state.hull,
        shipShield: s.ship.state.shield,
        roll: s.roll.map((r) => r.name),
        wallet: s.wallet.credits,
      }),
    };
    if (new URLSearchParams(window.location.search).get('smoke') === '1') {
      void runCameraSmoke().then((result) => {
        const pre = document.createElement('pre');
        pre.id = 'bm-smoke';
        pre.textContent = JSON.stringify(result);
        document.body.appendChild(pre);
        void fetch('/__smoke__', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(result),
        }).catch(() => {});
      });
    }
  }

  /** Sort helper: build a TargetGroup from distance-keyed rows, ascending. */
  const makeGroup = (
    title: string,
    rows: { dist: number; label: string; sub: string; select: () => void }[],
  ): TargetGroup => ({
    title,
    items: rows
      .sort((a, b) => a.dist - b.dist)
      .map(({ label, sub, select }) => ({ label, sub, select })),
  });

  /** M4: build the categorised TargetGroup list for the target menu (live distances). */
  const buildTargetGroups = (shipPos: Vec3): TargetGroup[] => {
    const groups: TargetGroup[] = [];

    // --- STARS / PLANETS / MOONS / STRUCTURES from the world manifest ---
    type Row = { dist: number; label: string; sub: string; select: () => void };
    const starRows: Row[] = [], planetRows: Row[] = [], moonRows: Row[] = [], structRows: Row[] = [];
    for (const b of s.world.bodies) {
      const dist = distance(b.worldPos, shipPos);
      const row: Row = { dist, label: b.entry.name, sub: fmtDistance(dist), select: () => { s.targets.select(b.entry.name); } };
      if (b.entry.name === 'DeathStar') structRows.push(row);
      else if (b.entry.parent === null) starRows.push(row);
      else if (b.entry.parent === 'Sol') planetRows.push(row);
      else moonRows.push(row);
    }
    for (const [title, rows] of [['STARS', starRows], ['PLANETS', planetRows], ['MOONS', moonRows], ['STRUCTURES', structRows]] as [string, Row[]][]) {
      const g = makeGroup(title, rows);
      if (g.items.length > 0) groups.push(g);
    }

    // --- STATIONS ---
    const stationRows: Row[] = [];
    for (const spec of STATIONS) {
      const wp = s.stations.worldPos(spec, s.world);
      const dist = distance(wp, shipPos);
      stationRows.push({ dist, label: spec.name, sub: fmtDistance(dist), select: () => { s.targets.lockCustom(spec.name, () => s.stations.worldPos(spec, s.world)); } });
    }
    const stationsGroup = makeGroup('STATIONS', stationRows);
    if (stationsGroup.items.length > 0) groups.push(stationsGroup);

    // --- SIGNALS ---
    const sigRows: Row[] = [];
    for (const sig of s.signalMgr.signals) {
      const dist = distance(sig.pos, shipPos);
      sigRows.push({ dist, label: sig.title, sub: `${sig.kind.toUpperCase()} · ${fmtDistance(dist)}`, select: () => { s.targets.lockSignal(sig); } });
    }
    const signalsGroup = makeGroup('SIGNALS', sigRows);
    if (signalsGroup.items.length > 0) groups.push(signalsGroup);

    // --- CONTACTS (NPCs) ---
    const contactRows: Row[] = [];
    for (const n of s.npcs) {
      const dist = distance(n.pos, shipPos);
      const id = n.id;
      contactRows.push({ dist, label: npcName(id), sub: fmtDistance(dist), select: () => { s.targets.lockCustom(npcName(id), () => s.npcs.find((x) => x.id === id)?.pos ?? null); } });
    }
    const contactsGroup = makeGroup('CONTACTS', contactRows);
    if (contactsGroup.items.length > 0) groups.push(contactsGroup);

    return groups;
  };

  const handleToggles = (snap: ReturnType<Input['consume']>): void => {
    if (snap.verify && s.verificationModule.visible) {
      s.verificationModule.dismiss();
      snap.verify = false;
    } else if (snap.verify && verifyAvailable()) {
      performVerify();
      snap.verify = false;
      snap.toggleRotAssist = false;
    }
    if (snap.toggleCamera) {
      s.camRig.toggle();
      s.hud.setMode(s.camRig.mode);
      s.cockpit.setMode(s.camRig.mode);
    }
    // M4: Tab opens / closes the target menu.
    // ITEM 2: free the cursor when the menu opens so overlay rows are clickable.
    if (snap.toggleTargetMenu) {
      s.targetMenu.toggle(buildTargetGroups(s.ship.worldPos));
      if (s.targetMenu.isOpen) document.exitPointerLock();
    }
    if (snap.targetNext) { s.targets.next(); s.audio.uiTick(); }
    if (snap.targetPrev) { s.targets.prev(); s.audio.uiTick(); }
    // M3: N nav-locks the nearest live signal for heading guidance (or no-op if none).
    if (snap.targetSignal) s.targets.lockSignal(s.nearestSignal?.signal ?? null);
    if (snap.toggleSupercruise) {
      const wasActive = s.cruise.active;
      if (wasActive) {
        // BUG-FIX: evaluate the drop-in decision BEFORE toggling so the drop path
        // (now via the state-independent shouldDropIntoSignal) runs coherently with
        // cruise still engaged, then disengage to NORMAL.
        dropOutOfCruise(); // B2: signal drop-in OR surface drop
        s.cruise.toggle();
      } else {
        s.cruise.toggle();
        if (s.signalMgr.active) leaveEncounter(); // re-engaging cruise clears the pocket
        s.director?.onEvent({ type: 'CRUISE_ENGAGED' }); // CAMPAIGN W1: title-card beat
      }
    }
    if (snap.toggleLegend) s.overlay.toggleLegend();
    // ITEM 3: O toggles the orbital-lines overlay.
    if (snap.toggleOrbitLines) s.orbitLines.toggle();
  };


  const buildHudFrame = (
    snap: ReturnType<Input['consume']>,
    shipPos: Vec3,
    dock: DockingState,
    dockCtx: StationContext | null,
  ): HudFrame => {
    const info = s.targets.info(shipPos, s.ship.velocity);
    const targetScreen = info ? projectDelta(tmpVec, info.worldPos, shipPos, s.bundle.camera) : null;
    let progradeScreen: HudFrame['progradeScreen'] = null;
    if (s.ship.speed > 1e-4) {
      const v = s.ship.velocity;
      tmpVec.set(v.x, v.y, v.z).normalize().multiplyScalar(PROGRADE_PROJECT_DIST);
      const p = Hud.project(tmpVec, s.bundle.camera);
      if (!p.behind) progradeScreen = { x: p.x, y: p.y };
    }
    const near = s.cruise.active ? s.world.nearestSurface(s.ship.worldPos) : null;
    return {
      throttle: snap.throttle,
      speedKmS: s.ship.speed,
      assist: s.ship.assist,
      // M3 FLIGHT-FEEL A2/A4/A5: rotational assist, power pips, throttle blue-zone.
      rotAssist: s.ship.rotAssist,
      pips: s.ship.pips,
      blueLo: s.ship.spec.optimalSpeedLo,
      blueHi: s.ship.spec.optimalSpeedHi,
      inBlueZone: s.ship.inBlueZone,
      target: info,
      targetScreen,
      progradeScreen,
      cruise: s.cruise.state === 'NORMAL' ? 'NORMAL' : 'SUPERCRUISE',
      spoolPercent: s.cruise.spoolPercent,
      canSafeDisengage: s.cruise.canDropToSurface(
        near ? near.clearanceKm : null,
        near ? near.radiusKm : null,
      ),
      radarBlips: updateRadarBlips(shipPos),
      // M2 P1: ship identity + state bars (0..1 fractions).
      shipName: s.ship.spec.name,
      hull01: s.ship.state.hull / s.ship.spec.hullMax,
      shield01: s.ship.state.shield / s.ship.spec.shieldMax,
      fuel01: s.ship.state.fuel / s.ship.spec.fuelMax,
      energy01: s.ship.state.energy / s.ship.spec.energyMax,
      // M2 P4/P5: combat lock HP + Roll + economy (spread helpers keep this lean).
      ...combatHudFields(s.combatLock, s.hp, s.roll, s.combatHitFlash, s.combatDmgFlash),
      ...econHudFields(s.wallet, s.cargo, s.activeMission),
      // H3: combat readability — health bar, off-screen markers, lead pip, dmg dir.
      ...combatReadabilityFields(
        s.combatLock, s.hp, s.npcs, shipPos, s.bundle.camera,
        s.combatDmgDirAngle, s.combatDmgDirFade, s.signalMgr.active !== null,
      ),
      // M3 WORLD B3/B6: signal readout + objective line + B-NARRATIVE drift-log.
      ...signalHudFields(shipPos),
      // AAA-02: the Verification Ledger rows (carved names + status).
      ledger: s.ledger,
      // ITEM 3: orbit-lines indicator state.
      orbitLinesOn: s.orbitLines.isVisible,
      // M4 CONTROL-FEEL: stick indicator — hidden while docked / menus open.
      stick:
        dock.phase === 'DOCKED' || s.targetMenu.isOpen || s.svc.isOpen || s.pauseMenu.isOpen
          ? null
          : s.ship.stickDeflection,
      // M2 P2: docking guidance.
      dockPhase: dock.phase,
      dockStationName: dockCtx ? dockCtx.spec.name : null,
      dockDistanceKm: dockCtx ? dockCtx.kin.distanceKm : 0,
      dockSpeedKmS: dockCtx ? dockCtx.kin.speedKmS : 0,
      dockPromptText: dockPrompt(dock.phase, dockCtx),
    };
  };

  /**
   * M3 WORLD B3/B6: signal + objective + drift-log HUD fields. Shows the nearest
   * live signal's label/threat/distance, a `DROP [X]` prompt when droppable, a
   * `SCOOP [T]` prompt when a canister is in range, the active objective line, and
   * the derelict drift-log while dropped in (B-NARRATIVE).
   *
   * FIX 2: droppable also fires when the LOCKED nav-target is the signal being
   * approached (target.isSignal + shouldDropIntoSignal via canDropToSignal). This
   * makes the drop prompt appear reliably when destination-targeted — the locked
   * target overrides the raw nearestSignal readout so the window is always visible.
   */
  const verifyGuidance = (objective: Objective | null): string | null => {
    if (s.verificationModule.visible) return null;
    if (verifyAvailable()) {
      return `ALIGN THE MARKER, THEN PRESS [${bindingLabel('verify')}] TO VERIFY THE RECORD.`;
    }
    const latest = s.ledger[s.ledger.length - 1];
    if (!latest) return null;
    const suffix = objective ? ' Follow the active task.' : ' Resume flight.';
    return latest.status === 'CONTRADICTION'
      ? `✕ CONTRADICTION: the wall says dead; another official record says alive.${suffix}`
      : `✓ TRUTH: the carving matches the record.${suffix}`;
  };

  const signalHudFields = (
    shipPos: Vec3,
  ): Pick<
    HudFrame,
    | 'signalLabel'
    | 'signalDistanceKm'
    | 'signalThreat'
    | 'signalPrompt'
    | 'scoopPrompt'
    | 'verifyPrompt'
    | 'verifyStatus'
    | 'verifyGuidance'
    | 'objectiveText'
    | 'driftLog'
  > => {
    const near = s.nearestSignal;
    const closing = near ? closingSpeedTo(shipPos, s.ship.velocity, near.signal.pos) : 0;
    const active = s.signalMgr.active;
    const verifyResult =
      active && !s.verifiedIds.has(active.signal.id) ? verifySignal(active.signal) : null;
    const nearDroppable = near
      ? s.cruise.canDropToSignal(near.distanceKm, near.signal.radiusKm, closing)
      : false;
    // FIX 2: also show drop prompt when the locked nav-target IS a signal and
    // the ship is within its drop band (works even if nearestSignal disagrees).
    const targetInfo = s.targets.info(shipPos, s.ship.velocity);
    const targetClosing = targetInfo ? targetInfo.closingSpeed : 0;
    const targetDroppable =
      targetInfo?.isSignal === true
        ? s.cruise.canDropToSignal(
            targetInfo.distanceKm,
            near?.signal.radiusKm ?? null,
            targetClosing,
          )
        : false;
    const droppable = nearDroppable || targetDroppable;
    const scoopNode = s.signalMgr.active ? s.signalMgr.nearestSalvage(shipPos, 12) : null;
    const objective = activeObjective();
    const hintsEnabled = getSettings().gameplay.hintsEnabled;
    return {
      signalLabel: near ? `${near.signal.title}` : null,
      signalDistanceKm: near ? near.distanceKm : 0,
      signalThreat: near ? near.signal.threatLevel : 0,
      signalPrompt: droppable ? `DROP [${bindingLabel('toggleSupercruise')}]` : '',
      scoopPrompt: scoopNode ? `SCOOP [${bindingLabel('scoop')}]` : '',
      verifyPrompt: verifyResult && verifyResult.status !== 'PENDING'
        ? `VERIFY [${bindingLabel('verify')}]`
        : '',
      verifyStatus: verifyResult && verifyResult.status !== 'PENDING' ? verifyResult.status : null,
      verifyGuidance: hintsEnabled ? verifyGuidance(objective) : null,
      objectiveText: hintsEnabled ? objectiveHudLine(objective) : null,
      driftLog: s.driftLog,
    };
  };

  const sunGlareDir = new THREE.Vector3();
  const glareCamPos = new THREE.Vector3();
  const glareCamToCenter = { x: 0, y: 0, z: 0 };

  const updateSunLight = (shipPos: Vec3): void => {
    const sol = s.world.get('Sol');
    if (!sol) return;
    const dx = sol.worldPos.x - shipPos.x;
    const dy = sol.worldPos.y - shipPos.y;
    const dz = sol.worldPos.z - shipPos.z;
    // WAVE 2a: a DirectionalLight only cares about direction (position − target), so
    // rescaling the huge Sol delta to a fixed distance up-sun leaves the shading
    // pixel-identical while making the sun-aligned ortho SHADOW camera's near/far
    // tractable at bubble scale (floating origin keeps the target at the origin).
    const len = Math.hypot(dx, dy, dz) || 1;
    const k = SUN_SHADOW_LIGHT_DISTANCE_KM / len;
    s.bundle.sunLight.position.set(dx * k, dy * k, dz * k);
    s.bundle.sunLight.target.position.set(0, 0, 0);
    s.bundle.sunLight.target.updateMatrixWorld();
  };

  /**
   * WAVE 2a: drive the Sol-only glare sprite. Sol's render direction is the
   * (normalised) sun-light position; occlusion is the min over every body disc
   * (Sol itself excluded) of how much of Sol that body leaves visible from the
   * camera — so a planet crossing in front of Sol snuffs the glare.
   */
  const updateSunGlare = (shipPos: Vec3): void => {
    sunGlareDir.copy(s.bundle.sunLight.position).normalize();
    s.bundle.camera.getWorldPosition(glareCamPos);
    let occlusion = 1;
    for (const b of s.world.bodies) {
      if (b.entry.name === 'Sol') continue;
      glareCamToCenter.x = b.worldPos.x - shipPos.x - glareCamPos.x;
      glareCamToCenter.y = b.worldPos.y - shipPos.y - glareCamPos.y;
      glareCamToCenter.z = b.worldPos.z - shipPos.z - glareCamPos.z;
      occlusion = Math.min(occlusion, sphereOcclusion01(sunGlareDir, glareCamToCenter, b.entry.radiusKm));
      if (occlusion <= 0) break;
    }
    s.sunGlare.update(s.bundle.camera, sunGlareDir, occlusion);
  };

  const shouldSkipFrame = (now: number, lastPresented: number): boolean => {
    const cap = getSettings().display.frameCap;
    if (cap <= 0) return false;
    return now - lastPresented < 1000 / cap;
  };

  /**
   * M2 P2 / FIX-DOCKING 2: step the docking state machine against the nearest
   * station, drive auto-approach easing while CLEARED/APPROACH, drive the services
   * overlay on the DOCKED edge, and park / push-off on capture / undock.
   * Returns the active station context (or null) for the HUD frame.
   *
   * Auto-approach: once the player presses G and is cleared (CLEARED/APPROACH),
   * the client eases the ship toward the berth each frame (blending position +
   * bleeding velocity). When autoApproachDock() signals capture-ready, we force
   * the FSM to capture by ensuring speed <= DOCK_MAX_SPEED_KMS in the kinematics
   * used for this tick — achieved by parking (zeroing vel) before calling stepDocking
   * so the pure FSM sees speed=0 and transitions to DOCKED cleanly.
   */
  const stepDockingTick = (snap: ReturnType<Input['consume']>, dt: number): StationContext | null => {
    const fwd = rotateVec3(s.ship.orientation, vec3(0, 0, -1));

    // FIX-DOCKING 2: run auto-approach when cleared/approaching and not yet docked.
    const autoApproachActive =
      (s.dock.phase === 'CLEARED' || s.dock.phase === 'APPROACH') &&
      s.dock.stationId !== null &&
      !s.cruise.active;

    if (autoApproachActive && s.dock.stationId !== null) {
      const approachSpec = stationById(s.dock.stationId);
      if (approachSpec) {
        const approachPos = s.stations.worldPos(approachSpec, s.world);
        const captureReady = s.ship.autoApproachDock(approachPos, dt);
        s.ship.resetStick(); // M4: stick has no authority during auto-approach
        // If auto-approach says we're capture-ready, park now so the FSM sees
        // speed=0 and distance within dockingRadius => transitions to DOCKED.
        if (captureReady) s.ship.parkAtDock();
      }
    }

    const ctx = nearestStationContext(
      STATIONS,
      (spec) => s.stations.worldPos(spec, s.world),
      s.ship.worldPos,
      s.ship.velocity,
      fwd,
    );
    const docked = s.dock.phase === 'DOCKED';
    const intent = { request: snap.dockRequest && !docked, undock: snap.dockRequest && docked };
    const prev = s.dock.phase;
    s.dock = stepDocking(s.dock, ctx, intent);
    if (s.dock.phase === 'DOCKED' && prev !== 'DOCKED' && ctx) {
      s.input.zeroThrottle(); // ITEM 1: cut throttle to zero on dock capture
      s.svc.show(ctx.spec);
      document.exitPointerLock(); // ITEM 2: free cursor so services menu is clickable
      s.audio.dock();
      emitObjective({ type: 'DOCKED_AT', stationId: ctx.spec.id }); // B6 deliver tasks
    }
    if (s.dock.phase !== 'DOCKED' && s.svc.isOpen) s.svc.hide();
    // FIX-DOCKING 1: parkAtDock on FSM APPROACH->DOCKED edge (belt-and-suspenders;
    // autoApproachDock already parked when capture-ready, but covers manual capture).
    if (s.dock.phase === 'DOCKED' && prev !== 'DOCKED') s.ship.parkAtDock();
    if (s.dock.phase === 'IDLE' && prev === 'UNDOCKING') {
      s.ship.pushOff(fwd);
      s.audio.undock(); // undock clamp release (sampled cue first; soft procedural fallback)
      if (s.deathPhase === 'ALIVE') persist(); // M2 P7: persist on undock (N2: + cloud mirror)
    }
    return ctx;
  };

  /** Fixed-step accumulator for NPC combat physics (mirrors player's 120 Hz loop). */
  let npcCombatAccum = 0;
  const NPC_SIM_DT = 1 / 120;

  // --- AUDIO: supercruise lifecycle + hull-critical loop (client hooks; all cues
  // degrade to silence when their sampled asset is absent). Watched off the cruise
  // state each frame so every disengage path (drop, death, dock) is covered. ---
  const HULL_CRITICAL_FRAC = 0.25;
  let prevCruiseState: CruiseState = 'NORMAL';
  let hullCriticalActive = false;
  const stepAudioState = (): void => {
    const cruise = s.cruise.state;
    if (cruise !== prevCruiseState) {
      if (prevCruiseState === 'NORMAL' && cruise === 'SPOOLING') s.audio.cruiseSpool();
      else if (cruise === 'SUPERCRUISE') { s.audio.cruiseEngage(); s.audio.cruiseTunnelStart(); }
      else if (cruise === 'NORMAL') { s.audio.cruiseDrop(); s.audio.cruiseTunnelStop(); }
      prevCruiseState = cruise;
    }
    const hull01 = s.ship.state.hull / s.ship.spec.hullMax;
    const critical = s.deathPhase === 'ALIVE' && hull01 < HULL_CRITICAL_FRAC;
    if (critical && !hullCriticalActive) { s.audio.hullCriticalStart(); hullCriticalActive = true; }
    else if (!critical && hullCriticalActive) { s.audio.hullCriticalStop(); hullCriticalActive = false; }
  };

  /** Combat Stage 1: advance every NPC toward its destination station; flip leg on
   * arrival.  enc-* hostiles (non-victims) fly under 6DOF physics with pursuit AI. */
  const stepTraffic = (dt: number, shipPos: Vec3): void => {
    // --- kinematic lane traffic (npc-* and sta-* and enc-* victims) ---
    for (let i = 0; i < s.npcs.length; i++) {
      const n = s.npcs[i];
      if (!n) continue;
      if (n.id.startsWith('enc-') && !isVictim(n.id)) continue; // handled below
      const dest = stationById(n.destId);
      if (!dest) continue;
      const dp = s.stations.worldPos(dest, s.world);
      let stepped = stepNpc(n, dp, dt);
      if (distance(stepped.pos, dp) <= NPC_ARRIVAL_SNAP_KM) stepped = nextLeg(stepped, STATIONS);
      s.npcs[i] = stepped;
    }

    // --- 6DOF physics for enc-* hostiles ---
    npcCombatAccum += dt;
    // Compute AI commands once per frame (pure; reused across substeps).
    const hostileIndices: number[] = [];
    const commands = new Map<string, ReturnType<typeof computeNpcCombatCommand>>();
    // Build the living hostile list once (allies array for separation/role).
    const livingHostiles = s.npcs.filter(
      (x) => x.id.startsWith('enc-') && !isVictim(x.id),
    );
    for (let i = 0; i < s.npcs.length; i++) {
      const n = s.npcs[i];
      if (!n || !n.id.startsWith('enc-') || isVictim(n.id)) continue;
      hostileIndices.push(i);
      const cd = npcFireCd.get(n.id) ?? 0;
      const npcNearest = s.world.nearestByCenter(n.pos);
      const npcHp = s.hp.get(n.id);
      const npcEnv: NpcCombatEnv = {
        simTime,
        nearestBody: npcNearest ? { pos: npcNearest.worldPos, radiusKm: npcNearest.radiusKm } : null,
        // Stage 3: allies (other living hostiles, excludes self) + hull fraction.
        allies: livingHostiles
          .filter((x) => x.id !== n.id)
          .map((x) => ({ id: x.id, pos: x.pos })),
        selfHullFrac: npcHp ? npcHp.hull / npcHp.hullMax : 1,
      };
      commands.set(n.id, computeNpcCombatCommand(
        { pos: n.pos, vel: n.vel, orientation: n.orientation, id: n.id },
        { pos: shipPos, vel: s.ship.velocity },
        n.shipType,
        cd,
        npcEnv,
      ));
    }

    while (npcCombatAccum >= NPC_SIM_DT) {
      npcCombatAccum -= NPC_SIM_DT;
      for (const i of hostileIndices) {
        const n = s.npcs[i];
        if (!n) continue;
        // Lazily create body on first step (uses current pos/vel from NpcShip).
        if (!s.npcBodies.has(n.id)) {
          s.npcBodies.set(n.id, makeCombatBody(n.shipType, n.pos, n.vel));
        }
        const body = s.npcBodies.get(n.id)!;
        const cmd = commands.get(n.id)!;
        const { npc: npc2, body: body2 } = stepNpcCombatPhysics(n, body, cmd, NPC_SIM_DT);
        s.npcs[i] = npc2;
        s.npcBodies.set(n.id, body2);
      }
    }
  };

  /** B-NARRATIVE/B5: faction tag for an encounter ship (enc-*), or null otherwise. */
  const factionOf = (id: string): string | null => {
    const enc = s.signalMgr.active;
    if (!enc) return null;
    const ship = [...enc.contents.hostiles, ...enc.contents.victims].find((e) => e.id === id);
    return ship?.faction ?? null;
  };

  /** True if this id is a distress victim in the active encounter (must be protected). */
  const isVictim = (id: string): boolean =>
    s.signalMgr.active?.contents.victims.some((v) => v.id === id) ?? false;

  /**
   * M2 P4 / M3 WORLD B5 / B-NARRATIVE: named, sombre death. Push to the Roll,
   * despawn the NPC + its HP/mesh, reuse the death cue. For an encounter ship,
   * emit the objective event: a slain hostile counts toward bounty (KILLED_NPC);
   * a slain DISTRESS VICTIM fails the rescue and is named on the Roll.
   */
  const killNpc = (n: NpcShip): void => {
    s.roll.unshift({ name: npcName(n.id), age: 0 });
    if (isVictim(n.id)) {
      s.objectives = s.objectives.map((o) =>
        o.kind === 'rescue' && o.status === 'ACTIVE' ? failObjective(o) : o,
      );
    } else if (n.id.startsWith('enc-')) {
      emitObjective({ type: 'KILLED_NPC', faction: factionOf(n.id) ?? 'unknown' });
    }
    s.npcs = s.npcs.filter((x) => x.id !== n.id);
    s.hp.delete(n.id);
    s.npcBodies.delete(n.id);
    s.npcRenderer.remove(n.id);
    s.audio.death();
  };

  /** Test a bolt against its valid targets; apply shield->hull damage. Player bolts
   * hit NPCs (kill on hull=0); AAA-14 hostile bolts (ownerId != 'player') hit the
   * ship via takeDamage. */
  const resolveHit = (p: Projectile, shipPos: Vec3): boolean => {
    if (p.ownerId !== 'player') {
      if (!projectileHits(p, shipPos, HIT_RADIUS_KM)) return false;
      // W1.1: dead/respawning ships take no further damage (grace = 5s).
      if (s.deathPhase !== 'ALIVE') return true; // bolt consumed, no effect
      if (!getSettings().gameplay.noFailReadingMode) s.ship.takeDamage(NPC_BOLT_DAMAGE);
      s.combatFx.spawnImpact(shipPos, HOSTILE_BOLT_COLOR);
      s.audio.incomingHit();
      s.combatShakeImpulse = Math.max(s.combatShakeImpulse, 0.6);
      s.combatDmgFlash = 0.25; // FIX 3: incoming-hit cue
      // H3: record angle from ship toward attacker bolt origin for edge indicator.
      s.combatDmgDirAngle = Math.atan2(p.pos.z - shipPos.z, p.pos.x - shipPos.x);
      s.combatDmgDirFade = 1.0;
      return true; // bolt consumed
    }
    for (const n of s.npcs) {
      if (!projectileHits(p, n.pos, HIT_RADIUS_KM)) continue;
      const hp = s.hp.get(n.id);
      if (!hp) continue;
      const next = applyDamage(hp, BOLT_DAMAGE);
      s.hp.set(n.id, next);
      s.combatFx.spawnImpact(p.pos, PLAYER_BOLT_COLOR);
      s.npcRenderer.flash(n.id);
      s.audio.hit();
      s.combatHitFlash = 0.35; // FIX 3: outgoing hit confirmation
      if (isDead(next)) {
        s.audio.kill();
        killNpc(n);
      }
      return true; // bolt consumed
    }
    return false;
  };

  /** Fire if the trigger is down, off cooldown, and energy allows.
   * FIX 2: when a combat lock is active, fire toward the first-order intercept
   * point (aimDirection) instead of straight forward. */
  const maybeFire = (snap: ReturnType<Input['consume']>, shipPos: Vec3, fwd: Vec3): void => {
    // Wave 3: the cadence + energy cost come from the active loadout's PRIMARY
    // fitted weapon (falling back to the legacy bolt when nothing is fitted), so
    // swapping a pulse for a railgun visibly changes how the player ship fires.
    const primary = s.effective.weapons[0];
    const energyCost = primary?.energyCost ?? WEAPON_ENERGY_COST;
    const cooldown = primary?.cooldownSec ?? FIRE_COOLDOWN_SEC;
    if (!snap.fire || s.fireCooldown > 0 || s.ship.state.energy < energyCost) return;
    let dir = fwd;
    if (s.combatLock) {
      const locked = s.npcs.find((n) => n.id === s.combatLock!.id);
      if (locked) dir = aimDirection(shipPos, locked.pos, locked.vel, BOLT_SPEED_KMS);
    }
    s.projectiles.push(fireWeapon(shipPos, dir, 'player', s.boltSeed++));
    s.combatFx.spawnMuzzle(shipPos, PLAYER_BOLT_COLOR);
    s.combatShakeImpulse = Math.max(s.combatShakeImpulse, 0.15);
    s.fireCooldown = cooldown;
    s.ship.spendEnergy(energyCost);
    s.audio.fire();
  };

  /** Advance all bolts; drop expired + consumed-on-hit ones. */
  const advanceProjectiles = (dt: number, shipPos: Vec3): void => {
    const alive: Projectile[] = [];
    for (const p0 of s.projectiles) {
      const p = stepProjectile(p0, dt);
      if (projectileExpired(p) || resolveHit(p, shipPos)) continue;
      alive.push(p);
    }
    s.projectiles = alive;
  };

  /** AAA-14: per-hostile return-fire cooldown clocks (persist across frames). */
  const npcFireCd = new Map<string, number>();

  /** Combat Stage 1: hostile return-fire — enc-* hostiles shoot along their physics
   * facing when the AI command says shouldFire (aligned + in-range + off-cooldown). */
  const stepReturnFire = (dt: number, shipPos: Vec3): void => {
    if (!s.signalMgr.active) return;
    const rfLivingHostiles = s.npcs.filter(
      (x) => x.id.startsWith('enc-') && !isVictim(x.id),
    );
    for (const n of s.npcs) {
      if (!n.id.startsWith('enc-') || isVictim(n.id)) continue;
      const cd = Math.max(0, (npcFireCd.get(n.id) ?? 0) - dt);
      npcFireCd.set(n.id, cd);
      const rfNearest = s.world.nearestByCenter(n.pos);
      const rfHp = s.hp.get(n.id);
      const rfEnv: NpcCombatEnv = {
        simTime,
        nearestBody: rfNearest ? { pos: rfNearest.worldPos, radiusKm: rfNearest.radiusKm } : null,
        // Stage 3: allies (other living hostiles, excludes self) + hull fraction.
        allies: rfLivingHostiles
          .filter((x) => x.id !== n.id)
          .map((x) => ({ id: x.id, pos: x.pos })),
        selfHullFrac: rfHp ? rfHp.hull / rfHp.hullMax : 1,
      };
      const cmd = computeNpcCombatCommand(
        { pos: n.pos, vel: n.vel, orientation: n.orientation, id: n.id },
        { pos: shipPos, vel: s.ship.velocity },
        n.shipType,
        cd,
        rfEnv,
      );
      if (cmd.shouldFire) {
        // Fire along the NPC's actual facing direction (body -Z in world frame).
        const fwd = rotateVec3(n.orientation, vec3(0, 0, -1));
        s.projectiles.push(fireWeapon(n.pos, fwd, n.id, s.boltSeed++));
        s.combatFx.spawnMuzzle(n.pos, HOSTILE_BOLT_COLOR);
        npcFireCd.set(n.id, NPC_FIRE_COOLDOWN_SEC);
      }
    }
  };

  /**
   * H3: tick the damage-direction fade, debounce the lock-acquired cue, and
   * debounce the wave-inbound cue. Pure client state — no sim-core changes.
   */
  const stepCombatReadability = (dt: number): void => {
    // Fade out the damage-direction indicator over ~1.2 s.
    s.combatDmgDirFade = Math.max(0, s.combatDmgDirFade - dt / 1.2);
    // Debounced lock-acquired cue: fire only when lock id changes.
    const lockId = s.combatLock?.id ?? null;
    if (lockId !== null && lockId !== s.lastLockId) s.audio.lockAcquired();
    s.lastLockId = lockId;
    // Debounced wave-inbound cue: fire when waveIndex advances.
    const wi = s.signalMgr.active?.waveIndex ?? 0;
    if (wi > s.lastWaveIndex && s.signalMgr.active !== null) s.audio.waveInbound();
    s.lastWaveIndex = wi;
  };

  /** Age the Roll; drop notices past the hold window (non-skippable fade-out). */
  const ageRoll = (dt: number): void => {
    for (const r of s.roll) r.age += dt;
    s.roll = s.roll.filter((r) => r.age < ROLL_HOLD_SEC);
  };

  /** M2 P4: acquire the combat lock, fire on trigger, advance bolts, age the Roll. */
  const stepCombat = (dt: number, snap: ReturnType<Input['consume']>, shipPos: Vec3): void => {
    const fwd = rotateVec3(s.ship.orientation, vec3(0, 0, -1));
    s.combatLock = acquireCombatTarget(s.npcs, shipPos, fwd);
    s.fireCooldown = Math.max(0, s.fireCooldown - dt);
    s.combatShakeImpulse = Math.max(0, s.combatShakeImpulse - dt / 0.35);
    // FIX 3: tick flash timers down each frame.
    s.combatHitFlash = Math.max(0, s.combatHitFlash - dt);
    s.combatDmgFlash = Math.max(0, s.combatDmgFlash - dt);
    maybeFire(snap, shipPos, fwd);
    stepReturnFire(dt, shipPos); // AAA-14: hostiles shoot back
    advanceProjectiles(dt, shipPos);
    s.flightFeel.addImpulse(s.combatShakeImpulse);
    ageRoll(dt);
    stepCombatReadability(dt); // H3: fade dir indicator, debounce audio cues
  };

  const SCOOP_RANGE_KM = 12;

  /** M3 WORLD B3: scoop the nearest salvage canister -> addCargo + SCOOPED event. */
  const maybeScoop = (snap: ReturnType<Input['consume']>, shipPos: Vec3): void => {
    if (!snap.scoop || !s.signalMgr.active) return;
    const node = s.signalMgr.nearestSalvage(shipPos, SCOOP_RANGE_KM);
    if (!node) return;
    const got = s.signalMgr.scoop(node.id);
    if (!got) return;
    const before = cargoUsed(s.cargo);
    s.cargo = addCargo(s.cargo, got.commodityId, got.qty);
    const added = cargoUsed(s.cargo) - before;
    if (added > 0) {
      emitObjective({ type: 'SCOOPED', qty: added }); // B6 salvage tasks
      s.audio.scoopCapture(); // sampled scoop cue first; UI click stays as fallback
    }
  };

  /**
   * M3 WORLD B5 / Combat Stage 3: step the living world. Re-seed/decay/cull ambient
   * signals, recompute the nearest droppable signal, drive escalating wave spawns
   * (next wave triggers when ≤1 hostile remains), and complete the rescue once all
   * hostiles are dead while the victim survives.
   */
  const stepWorld = (dt: number, shipPos: Vec3): void => {
    const fwd = rotateVec3(s.ship.orientation, vec3(0, 0, -1));
    s.signalMgr.update(shipPos, dt, fwd);
    s.nearestSignal = s.signalMgr.nearest(shipPos);
    // Combat Stage 3: spawn the next hostile wave when the previous wave is mostly dead.
    const spawned = s.signalMgr.checkWaveAdvance(s.npcs, s.hp);
    for (const e of spawned) void s.npcRenderer.add(s.npcs.find((n) => n.id === e.id)!);
    checkRescue();
    checkEncounterCleared(); // CAMPAIGN W1: dogfight-beat advance trigger
  };

  /** CAMPAIGN W1: once an encounter's LAST wave's LAST hostile dies, emit
   * ENCOUNTER_CLEARED exactly once for the campaign reducer. */
  let lastClearedSignalId: string | null = null;
  const checkEncounterCleared = (): void => {
    const enc = s.signalMgr.active;
    if (!enc || enc.waves.length === 0) return;
    if (enc.waveIndex < enc.waves.length) return; // waves still pending
    const anyHostile = s.npcs.some((n) => n.id.startsWith('enc-') && !isVictim(n.id));
    if (anyHostile || lastClearedSignalId === enc.signal.id) return;
    lastClearedSignalId = enc.signal.id;
    s.director?.onEvent({ type: 'ENCOUNTER_CLEARED', signalId: enc.signal.id });
    music.playAftermathSting(); // aftermath sting once per clear (music bus, ducked under VO)
  };

  /** B5/B-NARRATIVE: a distress rescue succeeds when its victim outlives the hunters. */
  const checkRescue = (): void => {
    const enc = s.signalMgr.active;
    if (!enc || enc.signal.kind !== 'distress') return;
    const victimAlive = enc.contents.victims.some((v) => s.hp.has(v.id));
    const hostilesLeft = enc.contents.hostiles.some((h) => s.hp.has(h.id));
    if (victimAlive && !hostilesLeft) {
      // emitObjective already steps EVERY objective through stepObjective AND pays
      // the reward on the ACTIVE→COMPLETE transition. A second manual step here
      // double-advanced rescue objectives, so a multi-goal rescue completed by the
      // redundant pass skipped its payout entirely. Emit once; the reducer handles it.
      emitObjective({ type: 'RESCUED', signalId: enc.signal.id });
      enc.signal.kind = 'wreckfield'; // resolved: stop re-triggering the rescue check
    }
  };

  /** The active objective record (for the HUD line), or null. */
  const activeObjective = (): Objective | null =>
    s.objectives.find((o) => o.id === s.activeObjectiveId) ?? null;

  const bindingLabel = (action: BindingAction): string =>
    displayBindingKey(getSettings().controls.keymap[action]);

  const renderPresentedFrame = (showRadar: boolean): void => {
    const { camera, composer, renderer } = s.bundle;
    renderer.clear();
    composer.render();
    if (s.camRig.mode === 'cockpit') {
      renderer.clearDepth();
      // Share the world scene's live PMREM environment so the cockpit reads as lit.
      s.cockpit.render(renderer, camera, s.camRig, s.bundle.scene.environment);
    }
    if (showRadar) {
      // Derive the 3D scissor box from the live DOM frame so the disc tracks the
      // #hud-radar border wherever the layout puts it (bottom-right in chase mode,
      // bottom-centre in cockpit mode).
      const frameEl = document.getElementById('hud-radar');
      s.radar3d.render(renderer, frameEl?.getBoundingClientRect() ?? null);
    }
  };

  const syncPlayerShip = (orientation: Quat): void => {
    s.playerShip.root.position.set(0, 0, 0);
    s.playerShip.root.quaternion.set(
      orientation.x,
      orientation.y,
      orientation.z,
      orientation.w,
    );
    s.playerShip.root.visible = s.camRig.mode !== 'cockpit';
  };

  const frame = (): void => {
    const now = performance.now();
    if (shouldSkipFrame(now, lastPresented)) {
      requestAnimationFrame(frame);
      return;
    }
    lastPresented = now;
    const dt = Math.min((now - lastNow) / 1000, 0.1);
    lastNow = now;
    simTime += dt * SIM_TIME_SCALE;

    // CAMPAIGN W1: while a chapter card (or the pre-flight cold open) is up,
    // the sim HOLDS — render-only, like the DOCKED routing, so the world
    // freezes under the card and resumes cleanly on dismiss.
    if (s.pauseMenu.isOpen || s.chapterCard.isOpen || (s.director?.holdSim ?? false)) {
      const held = s.input.consume(); // drain so held keys don't pile into the resume frame
      // CAMPAIGN §8 — the silent Cenotaph Choice. At the cold-open wall, once the
      // comms has closed, the player's hand decides with no menu and no prompt. The
      // beat table splits the carve from the exit:
      //   [V] finishes the K — a single TAP counts (never gated behind a timed hold;
      //       the reduced-motion path). It does NOT leave the wall: it only sets the
      //       reading the departure will record, so the player may sit indefinitely.
      //   [G] undock — the SINGLE departure action: records the reading ('finished'
      //       if carved, else 'left' — half-cut) and advances to first flight in one
      //       press. Neither road is labelled or acknowledged (silence contract).
      if (s.director?.coldOpenChoicePending) {
        if (held.verify) s.director.carveCenotaph();
        else if (held.dockRequest) s.director.departCenotaph();
      }
      if (s.chapterCard.isOpen) s.chapterCard.tick(dt);
      s.commsPanel.tick(dt);
      music.setState(musicStateForSim(s, settingsScreen));
      const rsHold = s.ship.renderState();
      s.camRig.update(rsHold.orientation, dt, s.flightFeel.shake);
      syncPlayerShip(rsHold.orientation);
      renderPresentedFrame(false);
      requestAnimationFrame(frame);
      return;
    }

    s.input.tickThrottle(dt); // smooth held-W/S throttle ramp (before consume snapshots it)
    const raw = s.input.consume();
    // W1.1: while the vessel is lost (drifting or under the card), the ship
    // answers no one — controls read as a zeroed snapshot; toggles are skipped.
    checkDeath();
    stepDeath(dt);
    const dead = s.deathPhase === 'DRIFTING' || s.deathPhase === 'CARD';
    const snap = dead ? zeroInput() : raw;
    // CAMPAIGN W1: while comms choices are pending, 1/2 answer the radio —
    // never the power pips.
    if (s.commsPanel.choicesVisible) {
      snap.pipSys = false;
      snap.pipEng = false;
      snap.pipWep = false;
    }
    if (!dead) handleToggles(snap);
    s.commsPanel.tick(dt); // radio types while flying (diegetic)

    // M4 CONTROL-FEEL: zero the persistent stick whenever the player has no
    // mouse authority — pointer-lock loss (Esc/alt-tab) or any open menu. A
    // deflected stick must never keep commanding a turn in those states.
    if (snap.lookReleased || s.targetMenu.isOpen || s.svc.isOpen || s.commsPanel.choicesVisible)
      s.ship.resetStick();

    // M4 CONTROL-FEEL: accumulate the frame's mouse delta into the virtual stick
    // and stash its shaped output as the held rate command BEFORE stepping, so
    // the fixed-step torque servo consumes the FULL frame delta even on
    // zero-substep high-refresh frames (preserves the M1.1 BUG-1 fix).
    s.ship.applyLook(dt, snap);
    const wasBoosting = s.ship.boosting;

    // FLIGHT-2: proximity-scaled supercruise vs. Newtonian normal flight.
    // FIX-DOCKING 1: while DOCKED and not in supercruise, hold the ship rigidly
    // attached to the orbiting station each frame instead of running Newtonian
    // physics — player thrust is suppressed so the ship cannot drift away.
    if (s.cruise.active) {
      const near = s.world.nearestSurface(s.ship.worldPos);
      // FIX 1: pass target distance so speed bleeds down as we approach the locked
      // nav-target (signal/body/station), not just nearby massive bodies.
      const cruiseTargetInfo = s.targets.info(s.ship.worldPos, s.ship.velocity);
      const targetDistKm = cruiseTargetInfo?.distanceKm ?? null;
      // APPROACH polish: when deliberately navigating to a SIGNAL or custom
      // station/contact lock (not the default body target), scale the effective
      // throttle by facing alignment. Misaligned at high closing speed is the
      // overshoot recipe — the dist/8 ceiling only saves a dead-on approach.
      // Pure client-side input shaping; the LOCKED cruise model is untouched.
      // (isSignal is true for signal locks, false for custom station/contact
      // locks, and UNDEFINED for default body targets — damp only the first two:
      // a deliberate lock means a deliberate approach.)
      let effThrottle = Math.abs(snap.throttle);
      if (cruiseTargetInfo && cruiseTargetInfo.isSignal !== undefined) {
        const fwd = rotateVec3(s.ship.orientation, vec3(0, 0, -1));
        const to = sub(cruiseTargetInfo.worldPos, s.ship.worldPos);
        const d = Math.hypot(to.x, to.y, to.z) || 1;
        const align = (fwd.x * to.x + fwd.y * to.y + fwd.z * to.z) / d;
        // smoothstep from 0.5 (≥60° off) to 0.95 (≤18° off) → factor 0.25..1.
        const t = Math.max(0, Math.min(1, (align - 0.5) / 0.45));
        const factor = 0.25 + 0.75 * (t * t * (3 - 2 * t));
        effThrottle *= factor;
      }
      const speed = s.cruise.update(
        dt,
        near ? near.clearanceKm : null,
        effThrottle,
        targetDistKm,
      );
      s.ship.supercruiseStep(dt, speed, snap); // KINEMATIC translation + servo rotation
    } else if (s.dock.phase === 'DOCKED' && s.dock.stationId !== null) {
      const dockedSpec = stationById(s.dock.stationId);
      if (dockedSpec) {
        const dockedPos = s.stations.worldPos(dockedSpec, s.world);
        s.ship.holdAtDock(dockedPos);
        s.ship.resetStick(); // M4: no deflection may pre-load a turn for undock
      }
    } else {
      s.ship.step(dt, snap); // LINEAR Newtonian physics + ANGULAR torque (fixed 120Hz)
    }
    if (!wasBoosting && s.ship.boosting) s.audio.boost(); // A3: afterburner ignition cue

    // M3 FLIGHT-FEEL A6: flight metrics for juice + engine audio.
    const speed01 = Math.min(1, s.ship.speed / Math.max(1e-6, s.ship.effectiveMaxSpeedKmS));
    const thrustFrac = Math.min(1, Math.hypot(snap.strafe, snap.vertical, snap.throttle));
    const boost01 = s.ship.boost01;
    const calm = s.cruise.active; // supercruise: suppress FOV/shake/speed-lines
    s.flightFeel.update(dt, speed01, thrustFrac, boost01, simTime, calm);
    s.audio.setFlight(snap.throttle, speed01, s.ship.boosting);

    s.world.update(simTime);

    // COLLISION-1: surface floor vs the nearest body — the ship cannot pass
    // below ~1.01× radius; inward radial velocity is arrested.
    // FIX (proximity-wall bug): use nearestByCenter instead of
    // nearestSurface(pos, includeStar=true).  The surface-clearance selector
    // could return Sol (R=696,000 km), whose floor sits 703,560 km from Sol's
    // centre — an invisible wall far from any visible object.  Nearest-by-
    // centre applies the floor to the body the ship is actually flying toward.
    const hit = s.world.nearestByCenter(s.ship.worldPos);
    if (hit) {
      if (debugMode && hit.clearanceKm < hit.radiusKm * 0.02) {
        console.warn(`[collide] body=${hit.name} clearance=${hit.clearanceKm.toFixed(1)} km floor=${(hit.radiusKm * 1.01).toFixed(1)} km`);
      }
      s.ship.collide({ worldPos: hit.worldPos, radiusKm: hit.radiusKm });
    }

    // M2 P2: docking (authoritative positions) — before render so a captured
    // ship is parked in this frame's snapshot. Pass dt for auto-approach easing.
    const dockCtx = stepDockingTick(snap, dt);
    // M2 P3: advance NPC traffic (authoritative world positions).
    stepTraffic(dt, s.ship.worldPos);
    // M2 P4: combat — lock, fire, advance bolts, resolve hits (authoritative pos).
    stepCombat(dt, snap, s.ship.worldPos);
    // M3 WORLD B3/B5: scoop salvage, then step signals/encounters/objectives.
    maybeScoop(snap, s.ship.worldPos);
    stepWorld(dt, s.ship.worldPos);

    const rs = s.ship.renderState();
    const shipPos = rs.position;
    s.bodyRenderer.update(shipPos, simTime);
    s.belt.update(shipPos);
    s.stations.update(shipPos, s.world, simTime);
    s.npcRenderer.update(s.npcs, shipPos, dt);
    s.projRenderer.update(s.projectiles, shipPos);
    s.combatFx.update(dt, shipPos);
    // M3 WORLD B3: signal beacons + active-encounter salvage + derelict hull.
    s.signalRenderer.update(s.signalMgr.signals, shipPos, simTime, s.signalMgr.active?.signal.id ?? null);
    s.signalRenderer.updateDerelict(s.signalMgr.active ? s.signalMgr.active.signal.pos : null, shipPos);
    s.anomalyFx.update(s.signalMgr.signals, shipPos, simTime, dt);
    // AAA-10: the Cenotaph hero — pinned at the story signal, the carved-name landmark.
    const cenotaphSig = s.signalMgr.signals.find((sg) => sg.id === 'story-cenotaph-vigil');
    s.cenotaph.update(cenotaphSig ? cenotaphSig.pos : null, shipPos, simTime);
    // ITEM 3: orbital-lines overlay (default OFF; toggled by O key).
    s.orbitLines.setDerelicts(s.signalMgr.signals);
    s.orbitLines.update(shipPos, simTime);
    s.salvageRenderer.update(s.signalMgr.active ? s.signalMgr.active.salvage : [], shipPos, simTime);
    if (s.camRig.verifying && (snap.mouseDX || snap.mouseDY || snap.keyYaw || snap.keyPitch)) {
      s.camRig.cancelVerify();
    }
    s.camRig.tick(dt);
    advanceLedger(dt); // AAA-02: glyph-by-glyph ledger reveal
    s.verificationModule.tick(dt);
    s.camRig.update(rs.orientation, dt, s.flightFeel.shake); // A6 shake + M4 rotational spring
    syncPlayerShip(rs.orientation);
    s.flightFeel.applyFov(s.bundle.camera); // A6: dynamic speed/boost FOV
    followCamera(s.starfield, s.bundle.camera); // M1.1c: parallax-free, rotation-free
    // A6: speed-line streaks follow the camera, aligned to velocity (suppressed in SC).
    s.bundle.camera.getWorldPosition(tmpVec);
    s.speedLines.update(dt, tmpVec, s.ship.velocity, speed01, boost01, calm);
    // FIX-WARP: supercruise warp-stretch overlay — camera-parented so no pos copy needed;
    // log-curve intensity so tunnel is vivid at realistic cruise speeds (50k+ km/s).
    const warpSpoolFrac = s.cruise.spoolPercent / 100;
    const warpIntensity = warpSpoolFrac * (0.15 + 0.85 * Math.min(1, Math.log(1 + s.cruise.speedKmS / 50_000) / Math.LN2));
    s.supercruiseWarp.update(dt, s.cruise.active, s.cruise.speedKmS, warpSpoolFrac);
    // Drive cruise FOV widen from the same intensity so the widened tunnel reads immediately on engage.
    s.flightFeel.setCruiseIntensity(s.cruise.active ? warpIntensity : 0);
    stepAudioState(); // supercruise lifecycle cues + hull-critical alarm loop
    music.setState(musicStateForSim(s, settingsScreen));

    const hudFrame = buildHudFrame(snap, shipPos, s.dock, dockCtx);
    s.hud.update(hudFrame);
    s.cockpit.updateInstruments(hudFrame); // Part 2: drive the diegetic dash/console screens
    s.radar3d.update(hudFrame.radarBlips);
    updateSunLight(shipPos);
    updateSunGlare(shipPos); // WAVE 2a: Sol-only glare (after the sun dir is set)

    const grainT = s.bundle.postFx.uniforms.uTime; // AAA-19: animate film grain
    if (grainT) grainT.value = now / 1000;
    renderPresentedFrame(true);
    requestAnimationFrame(frame);
  };
  frame();
};

// Backstop for any un-handled loader rejection: a readable fatal error (console
// + on-page banner) instead of a silent blank canvas.
start().catch((err: unknown) => {
  console.error('[boot] fatal during startup', err);
  const banner = document.createElement('div');
  banner.style.cssText =
    'position:fixed;top:0;left:0;right:0;padding:12px 16px;background:#3a0a0a;' +
    'color:#ffb4b4;font:14px monospace;z-index:9999;white-space:pre-wrap;';
  banner.textContent =
    `BOOT FAILED: ${err instanceof Error ? err.message : String(err)}\n` +
    'Check the console for details (missing assets? run from a full clone).';
  document.body.appendChild(banner);
});
